import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Guid } from 'typescript-guid';
import { MedicalInquiry, MedicalInquiryFetchAttributes, MedicalInquiryStatus } from '@omni/classes/medical-inquiry/medical-inquiry.class';
import { DB_ALLDOCS_QUERY_OPTIONS, DB_KEY_PREFIXES, DB_SYNC_STATE_KEYS } from '@omni/config/pouch-db.config';
import { AuthenticationService } from '@omni/services/authentication.service';
import { DeviceService } from '@omni/services/device/device.service';
import { DiskService, OFFLINE_DATA_COUNT_ENTITY_NAME } from '@omni/services/disk/disk.service';
import { DynamicFormsService } from '@omni/services/dynamic-forms/dynamic-forms-service';
import { DynamicsClientService } from '../dynamics-client/dynamics-client.service';
import { fetchQueries } from '@omni/config/dynamics-fetchQueries';
import { FeatureActionsMap } from '@omni/classes/authentication/user.class';
import { LogService } from '@omni/services/logging/log-service';
import { ContactOfflineService } from '@omni/services/contact/contact.service';
import { ActivityService } from '@omni/services/activity/activity.service';
import { AppointmentActivity } from '@omni/classes/activity/appointment.activity.class';
import { differenceInHours } from 'date-fns';
import { DynamicForm, FormType } from '@omni/classes/dynamic-form/dynamic-form.class';
import { DynamicFormType } from '@omni/models/dynamic-form-component.model';
import { NotificationService, ToastStyle } from '@omni/services/notification/notification.service';
import { TranslateService } from '@ngx-translate/core';

const MEDICAL_INQUIRY_ENTITY = 'indskr_medicalinquiries';
const MEDICAL_INQUIRY_PRODUCT_ENTITY = 'indskr_medicalinquiryproducts';

/** Shape of sync state doc used for delta sync (lastUpdatedTime, PouchDB _id/_rev). */
interface MedicalInquirySyncState {
  _id?: string;
  _rev?: string;
  lastUpdatedTime?: number;
  /** Whether the last sync used this org's configured MI forms or the hardcoded core-field default. */
  formType?: DynamicFormType;
  /** Latest modifiedOn (ms) across the configured MI forms used for the last CONFIGUREDFORM sync. */
  lastFormModifiedOn?: number;
}

/** A pending product add or scrap from the MI form's Products subgrid (see MedicalInquiryService.upsertLinkEntityPayload). */
export interface MedicalInquiryProductChange {
  values?: Record<string, any>;
  entity?: string;
  linkedEntityId?: string;
  addedNewDataId?: string;
  formStringValue?: Record<string, string>;
}

export interface MedicalInquiryBatchSaveResult {
  /** The MI update's response, or the Error Dynamics returned for it; undefined when no form payload was sent. */
  miResult?: Record<string, unknown> | Error;
  addedProducts: Array<{ change: MedicalInquiryProductChange; guid: string }>;
  productErrors: Error[];
}

/**
 * Data service for Medical Inquiries.
 * Owns HTTP, offline storage, and entity CRUD.
 * Used by MedicalInquiryService, MedicalInquiryListComponent, and related components.
 */
@Injectable({
  providedIn: 'root'
})
export class MedicalInquiryDataService {
  medicalInquiries: MedicalInquiry[] = [];
  medicalInquiries$ = new BehaviorSubject<MedicalInquiry[]>([]);
  private readonly _syncProductsInFlight = new Map<string, Promise<Record<string, unknown>[]>>();

  constructor(
    private readonly authService: AuthenticationService,
    private readonly deviceService: DeviceService,
    private readonly disk: DiskService,
    private readonly dynamics: DynamicsClientService,
    private readonly dynamicFormsService: DynamicFormsService,
    private readonly logService: LogService,
    private readonly contactOfflineService: ContactOfflineService,
    private readonly activityService: ActivityService,
    private readonly notificationService: NotificationService,
    private readonly translate: TranslateService
  ) {}

  /**
   * Map medical inquiry product form values (lookupfields) to payload for createLinkedEntity.
   * Uses PascalCase navigation names and leading slash:
   * { "indskr_MedicalInquiry@odata.bind": "/indskr_medicalinquiries(guid)", "indskr_Product@odata.bind": "/products(guid)" }
   */
  mapMedicalInquiryProductForCreateLinkedEntity(values: Record<string, unknown>, medicalInquiryId?: string): Record<string, unknown> {
    const lookupfields = (values as Record<string, any>)?.lookupfields;
    const payload: Record<string, unknown> = {};
    if (Array.isArray(lookupfields)) {
      for (const lf of lookupfields) {
        if (lf?.name && lf?.entity && lf?.id) {
          const id = (lf.name === 'indskr_medicalinquiry' || lf.name === 'indskr_MedicalInquiry') && medicalInquiryId
            ? medicalInquiryId
            : lf.id;
          const bindKey = lf.name === 'indskr_medicalinquiry' ? 'indskr_MedicalInquiry@odata.bind'
            : lf.name === 'indskr_product' ? 'indskr_Product@odata.bind'
            : `${lf.name}@odata.bind`;
          payload[bindKey] = `/${lf.entity}(${id})`;
        }
      }
    }
    return payload;
  }

  /**
   * Map linked entity payload (with lookupfields array) to OData API format (@odata.bind).
   * Uses FK attribute names (indskr_medicalinquiryid, indskr_productid) and path without leading slash,
   * matching quote.data.service and email.service patterns.
   */
  mapLinkedEntityPayloadToApi(values: Record<string, unknown>): Record<string, unknown> {
    const { lookupfields, indskr_quantity, ...rest } = values as Record<string, any>;
    const apiPayload: Record<string, unknown> = { ...rest };

    // Use Dynamics lookup attribute names (indskr_medicalinquiry, indskr_product) - schema does not use ...id suffix
    const lookupToFk: Record<string, string> = {
      indskr_medicalinquiry: 'indskr_medicalinquiry',
      indskr_MedicalInquiry: 'indskr_medicalinquiry',
      indskr_product: 'indskr_product',
      indskr_Product: 'indskr_product'
    };

    if (Array.isArray(lookupfields) && lookupfields.length > 0) {
      for (const lf of lookupfields) {
        if (lf?.name && lf?.entity && lf?.id) {
          const bindKey = (lookupToFk[lf.name] ?? lf.name) + '@odata.bind';
          apiPayload[bindKey] = `${lf.entity}(${lf.id})`;
        }
      }
    }
    return apiPayload;
  }

  /**
   * Dynamics Web API expects PascalCase navigation property names on indskr_medicalinquiry
   * (e.g. indskr_Contact@odata.bind), not lowercase indskr_contact@odata.bind — the latter
   * triggers ODataException "undeclared property 'indskr_contact'".
   */
  private mapMiMainEntityLookupToODataBind(lf: {
    name: string;
    entity: string;
    id: string;
  }): { bindKey: string; collectionSegment: string } | null {
    if (!lf?.name || !lf?.id) return null;
    const id = String(lf.id).trim();
    if (!id) return null;
    const n = String(lf.name).toLowerCase().replace(/\s/g, '');

    if (n === 'indskr_contact' || n === 'contact') {
      return { bindKey: 'indskr_Contact@odata.bind', collectionSegment: 'contacts' };
    }
    if (n === 'indskr_address' || n === 'address') {
      return { bindKey: 'indskr_Address@odata.bind', collectionSegment: 'indskr_addresses' };
    }
    if (n === 'indskr_casetype' || n === 'casetype') {
      return { bindKey: 'indskr_CaseType@odata.bind', collectionSegment: 'indskr_expertcategorieses' };
    }
    if (n === 'indskr_appointment' || n === 'appointment') {
      return { bindKey: 'indskr_Appointment@odata.bind', collectionSegment: 'appointments' };
    }

    return {
      bindKey: `${lf.name}@odata.bind`,
      collectionSegment: String(lf.entity || '').replace(/^\//, '').replace(/\(.+$/, '') || String(lf.entity),
    };
  }

  /**
   * Resolve an appointment reference to its real Dynamics GUID. The reference comes from the MI's
   * rawData._indskr_appointment_value, which can be either a real GUID (online meeting) or an
   * offline_meeting_<uuid> id (Scenario B: meeting was created in the same offline session).
   *
   * After uploadOfflineMeetings runs, the in-memory appointment's .ID is swapped to the real
   * GUID while .offlineMeetingId is preserved as the back-reference. We use offlineMeetingId as
   * the lookup key.
   *
   * Returns the real GUID if resolvable; '' if the reference is an offline id whose meeting
   * hasn't synced yet (caller should defer the MI).
   */
  private resolveAppointmentRef(apptRef: string | undefined): string {
    if (!apptRef) return '';
    if (!apptRef.includes('offline')) return apptRef;
    const matched = this.activityService.activities?.find(
      a => a instanceof AppointmentActivity
        && (a as AppointmentActivity).offlineMeetingId === apptRef
        && !!a.ID
        && !a.ID.includes('offline')
    );
    return matched?.ID || '';
  }

  /**
   * Map form payload (with lookupfields array) to API payload (with @odata.bind format).
   * Null values are preserved as-is (e.g. cleared boolean fields should be sent as null, not false).
   * Forwards indskr_* scalars (plus state/status for scrap/submit). Skips underscore-prefixed and
   * OData annotation keys (e.g. @OData.Community.Display.V1.FormattedValue). Navigation binds
   * (*@odata.bind), e.g. indskr_Appointment@odata.bind from in-meeting MI create, are forwarded;
   * lookupfields mappings run after and may override the same bind key.
   */
  private mapFormPayloadToApiPayload(formPayload: Record<string, unknown>): Record<string, unknown> {
    const { lookupfields, ...rest } = formPayload as Record<string, any>;
    const apiPayload: Record<string, unknown> = {};

    const extraRootKeys = new Set(['statecode', 'statuscode']);
    for (const [k, v] of Object.entries(rest)) {
      if (k.endsWith('@odata.bind')) {
        apiPayload[k] = v;
        continue;
      }
      if (k.includes('@')) continue;
      if (k.startsWith('_')) continue;
      if (k.startsWith('indskr_') || k.startsWith('omp_') || extraRootKeys.has(k)) {
        apiPayload[k] = v;
      }
    }

    if (Array.isArray(lookupfields) && lookupfields.length > 0) {
      for (const lf of lookupfields) {
        if (lf?.name && lf?.id) {
          const mapped = this.mapMiMainEntityLookupToODataBind(lf);
          if (mapped) {
            apiPayload[mapped.bindKey] = `/${mapped.collectionSegment}(${String(lf.id).trim()})`;
          }
        }
      }
    }

    return apiPayload;
  }

  /**
   * Create a medical inquiry.
   * Accepts form payload (with lookupfields array) and maps to API format (@odata.bind).
   * @param formPayload Form payload from ind-dynamic-form (may include lookupfields array)
   * @returns API response with created record (typically includes indskr_medicalinquiryid)
   */
  async createMedicalInquiry(formPayload: Record<string, unknown>): Promise<Record<string, unknown>> {
    const apiPayload = this.mapFormPayloadToApiPayload(formPayload);
    return this.dynamics.createRequest({
      collection: 'indskr_medicalinquiries',
      data: apiPayload,
      returnRepresentation: true
    });
  }

  /**
   * Update a medical inquiry.
   * Uses Prefer: return=representation to receive the updated entity from the API.
   * @param id indskr_medicalinquiryid
   * @param formPayload Form payload from ind-dynamic-form (may include lookupfields array)
   * @returns The updated entity as returned by Dynamics, or undefined if the API did not return a body
   */
  async updateMedicalInquiry(id: string, formPayload: Record<string, unknown>): Promise<Record<string, unknown> | void> {
    const response = await this.dynamics.update(
      id,
      MEDICAL_INQUIRY_ENTITY,
      this._toMedicalInquiryUpdatePayload(formPayload),
      'return=representation'
    );
    return response as Record<string, unknown> | void;
  }

  private _toMedicalInquiryUpdatePayload(formPayload: Record<string, unknown>): Record<string, unknown> {
    const apiPayload = this.mapFormPayloadToApiPayload(formPayload) as Record<string, unknown>;
    delete apiPayload.indskr_medicalinquiryid;
    return apiPayload;
  }

  /**
   * Online save of product scraps, product adds and the MI update in one Dynamics $batch, queued in that order.
   * continueOnError keeps each outcome independent, like the separate calls this replaced. Added products are
   * stored locally here; callers report the results and refetch the MI.
   */
  async saveMedicalInquiryWithProducts(
    medicalInquiryId: string,
    productChanges: MedicalInquiryProductChange[],
    formPayload?: Record<string, unknown>
  ): Promise<MedicalInquiryBatchSaveResult> {
    const scraps = productChanges.filter(c => c.values?.statecode === 1 && c.linkedEntityId);
    const adds = productChanges.filter(c => c.values?.statecode !== 1 && !c.linkedEntityId && !c.values?.guid);
    const result: MedicalInquiryBatchSaveResult = { addedProducts: [], productErrors: [] };
    if (!scraps.length && !adds.length) {
      if (formPayload) result.miResult = (await this.updateMedicalInquiry(medicalInquiryId, formPayload)) || undefined;
      return result;
    }

    // No await until executeBatch: any Dynamics call made while the batch is open is swallowed into it.
    this.dynamics.startBatch({ returnRepresentation: true, includeAnnotations: '*' });
    try {
      for (const scrap of scraps) {
        const { statecode, statuscode } = scrap.values ?? {};
        this.dynamics.update(scrap.values?.guid ?? scrap.linkedEntityId, scrap.entity ?? MEDICAL_INQUIRY_PRODUCT_ENTITY, { statecode, statuscode });
      }
      for (const add of adds) {
        this.dynamics.createRequest({
          collection: add.entity ?? MEDICAL_INQUIRY_PRODUCT_ENTITY,
          data: this.mapMedicalInquiryProductForCreateLinkedEntity(add.values ?? {}, medicalInquiryId),
          returnRepresentation: true
        });
      }
      if (formPayload) {
        this.dynamics.update(medicalInquiryId, MEDICAL_INQUIRY_ENTITY, this._toMedicalInquiryUpdatePayload(formPayload), 'return=representation');
      }
    } catch (err) {
      this.dynamics.abortBatch();
      throw err;
    }
    const responses: unknown[] = await this.dynamics.executeBatch({ continueOnError: true });

    result.productErrors.push(...responses.slice(0, scraps.length).filter((r): r is Error => r instanceof Error));
    let allNamesResolved = true;
    for (let i = 0; i < adds.length; i++) {
      const response = responses[scraps.length + i];
      if (response instanceof Error) {
        result.productErrors.push(response);
        continue;
      }
      const guid = typeof response === 'string' ? response : (response as Record<string, any>)?.indskr_medicalinquiryproductid;
      if (!guid) {
        result.productErrors.push(new Error('Product create returned no id'));
        continue;
      }
      const nameResolved = await this.addProductAfterCreation(
        medicalInquiryId,
        adds[i].values ?? {},
        { indskr_medicalinquiryproductid: guid },
        adds[i].formStringValue
      );
      allNamesResolved &&= nameResolved;
      result.addedProducts.push({ change: adds[i], guid });
    }
    if (!allNamesResolved) await this.syncMedicalInquiryProductsForDisplay(medicalInquiryId);
    if (result.productErrors.length) this._notifyProductSaveFailed(result.productErrors);
    if (formPayload) result.miResult = responses[scraps.length + adds.length] as Record<string, unknown> | Error;
    return result;
  }

  /** Same toasts DynamicFormsService.createLinkedEntity raised per failed call, once per Save. */
  private _notifyProductSaveFailed(errors: Error[]): void {
    errors.forEach(err => this.logService.logError('MedicalInquiryDataService: product save failed', err));
    const isDuplicate = errors.some(err => /duplicate/i.test(err?.message ?? ''));
    const key = isDuplicate ? 'DUPLICATE_DETECTED' : 'FAILED_CREATING_LINKED_ENTITY_GENERIC_ERROR_MESSAGE';
    this.notificationService.notify(this.translate.instant(key), 'MedicalInquiryDataService', 'top', ToastStyle.DANGER, 3000);
  }

  /**
   * Upsert a medical inquiry from API response.
   * Uses MedicalInquiry.fromApiResponse to keep only model-needed fields.
   * Explicitly applies statuscode from response so the MI object reflects the updated state.
   * @returns Updated MedicalInquiry
   */
  upsertMedicalInquiryFromApiResponse(raw: Record<string, unknown>): MedicalInquiry {
    const mi = MedicalInquiry.fromApiResponse(raw);
    if (raw && raw['statuscode'] != null) {
      mi.statusCode = Number(raw['statuscode']);
      (mi.rawData as Record<string, unknown>)['statuscode'] = raw['statuscode'];
    }
    if (raw && (raw['statuscode_Formatted'] || raw['_statuscode_value@OData.Community.Display.V1.FormattedValue'])) {
      mi.statusCodeFormatted =
        (raw['statuscode_Formatted'] as string) ||
        (raw['_statuscode_value@OData.Community.Display.V1.FormattedValue'] as string) ||
        '';
      (mi.rawData as Record<string, unknown>)['statuscode_Formatted'] = mi.statusCodeFormatted;
    }
    this.upsertMedicalInquiry(mi);
    return mi;
  }

  /**
   * Create a medical inquiry product (linked entity).
   * Same pattern as createMedicalInquiry - uses Dynamics client directly with @odata.bind payload.
   */
  async createMedicalInquiryProduct(formPayload: Record<string, unknown>): Promise<Record<string, unknown>> {
    const { guid, ...payloadWithoutGuid } = formPayload as Record<string, any>;
    const apiPayload = this.mapLinkedEntityPayloadToApi(payloadWithoutGuid);
    return this.dynamics.createRequest({
      collection: MEDICAL_INQUIRY_PRODUCT_ENTITY,
      data: apiPayload,
      returnRepresentation: true
    });
  }

  /**
   * Create a medical inquiry product via direct Dynamics API call (for testing).
   * Uses the same payload format as Dynamics: navigation property names (PascalCase),
   * leading slash in @odata.bind paths, and ownerid bind.
   *
   * @param params productId, medicalInquiryId; or form payload with lookupfields
   * @returns Created record response
   */
  async createMedicalInquiryProductDirect(params: {
    productId?: string;
    medicalInquiryId?: string;
    lookupfields?: Array<{ name: string; entity: string; id: string }>;
  }): Promise<Record<string, unknown>> {
    let productId: string;
    let medicalInquiryId: string;

    if (params.lookupfields && Array.isArray(params.lookupfields)) {
      const productLf = params.lookupfields.find(lf => lf.name === 'indskr_product' || lf.name === 'indskr_Product');
      const medicalInquiryLf = params.lookupfields.find(lf => lf.name === 'indskr_medicalinquiry' || lf.name === 'indskr_MedicalInquiry');
      productId = productLf?.id ?? params.productId ?? '';
      medicalInquiryId = params.medicalInquiryId ?? medicalInquiryLf?.id ?? '';
    } else {
      productId = params.productId ?? '';
      medicalInquiryId = params.medicalInquiryId ?? '';
    }

    if (!productId || !medicalInquiryId) {
      throw new Error('createMedicalInquiryProductDirect: productId and medicalInquiryId are required');
    }

    const ownerId = this.authService.user?.systemUserID;
    const data: Record<string, unknown> = {
      'indskr_Product@odata.bind': `/products(${productId})`,
      'indskr_MedicalInquiry@odata.bind': `/indskr_medicalinquiries(${medicalInquiryId})`,
      statecode: 0,
      statuscode: MedicalInquiryStatus.DRAFT
    };
    if (ownerId) {
      data['ownerid@odata.bind'] = `/systemusers(${ownerId})`;
    }

    return this.dynamics.createRequest({
      collection: MEDICAL_INQUIRY_PRODUCT_ENTITY,
      data,
      returnRepresentation: true
    });
  }

  /**
   * Update a medical inquiry product (e.g. deactivate/scrap with statecode: 1).
   * For deactivate we only send statecode and statuscode.
   */
  async updateMedicalInquiryProduct(id: string, formPayload: Record<string, unknown>): Promise<void> {
    const payload = formPayload as Record<string, any>;
    const apiPayload: Record<string, unknown> = {};
    if (payload.statecode !== undefined) apiPayload.statecode = payload.statecode;
    if (payload.statuscode !== undefined) apiPayload.statuscode = payload.statuscode;
    await this.dynamics.update(id, MEDICAL_INQUIRY_PRODUCT_ENTITY, apiPayload);
  }

  /**
   * Fetch medical inquiries from API or load from DB when offline.
   * Active records only (statecode = 0).
   */
  async fetchMedicalInquiries(loadFromDbOnly = false): Promise<void> {
    if (!this.authService.hasFeatureAction(FeatureActionsMap.MEDICAL_INQUIRY_TOOL)) {
      return;
    }

    if (this.deviceService.isOffline || loadFromDbOnly) {
      await this.loadMedicalInquiriesFromDb();
      return;
    }

    await this.syncMedicalInquiriesFromServer();
  }

  /**
   * Resolve this org's MI fetch attribute list: core fields always included, plus whatever
   * fields this org's configured CREATE/EDIT/DISPLAY MI forms declare (e.g. a customer's own
   * custom fields). Falls back to core-only when no MI form is configured for this org at all.
   * Mirrors ContactService.fetchContactsForConfiguredDisplay / AccountDataService's
   * _getDynamicFormAndType + _getAccountsConfiguredDisplayFetchXML.
   */
  private _resolveMedicalInquiryAttributes(): { attributesXml: string; formType: DynamicFormType; formModifiedOn: number | null } {
    const forms = [
      this.dynamicFormsService.getFormDefinitionForEntity('indskr_medicalinquiry', FormType.CREATEFORM),
      this.dynamicFormsService.getFormDefinitionForEntity('indskr_medicalinquiry', FormType.EDITFORM),
      this.dynamicFormsService.getFormDefinitionForEntity('indskr_medicalinquiry', FormType.DISPLAYFORM),
    ].filter((form): form is DynamicForm => !!form);

    const attributeNames = new Set<string>(MedicalInquiryFetchAttributes);
    let formModifiedOn: number | null = null;

    for (const form of forms) {
      if (form.modiefiedOn) {
        const modifiedOnMs = new Date(form.modiefiedOn).getTime();
        if (!formModifiedOn || modifiedOnMs > formModifiedOn) formModifiedOn = modifiedOnMs;
      }
      if (!form.metadata?.length) continue;
      for (const tab of form.metadata) {
        if (!tab.controls?.length) continue;
        for (const control of tab.controls) {
          // Controls with no dataType and a subgrid are linked-entity subgrids (e.g. products),
          // fetched separately - skip them here, same rule Contact/Account use.
          if (control.dataType && control.attributeName) {
            attributeNames.add(control.attributeName);
          }
        }
      }
    }

    const attributesXml = Array.from(attributeNames).map(a => `<attribute name='${a}'/>`).join('\n    ');
    const formType = forms.length ? DynamicFormType.CONFIGUREDFORM : DynamicFormType.DEFAULTFORM;
    return { attributesXml, formType, formModifiedOn };
  }

  private async syncMedicalInquiriesFromServer(): Promise<void> {
    const syncState = await this.disk.getSyncState(DB_SYNC_STATE_KEYS.SYNC_MEDICAL_INQUIRIES);
    const { attributesXml, formType, formModifiedOn } = this._resolveMedicalInquiryAttributes();

    // isTrueFirstSync: this device has never completed an MI sync before, so no local/offline
    // history can exist yet - only here is a blind replace (no merge) of in-memory records and
    // the product store safe. isInitialSync below can also be forced true by a form config change
    // on a device that already has offline history (pending creates, deferred products) - that
    // case must go through the merge-aware path further down, not the blind-replace one.
    const isTrueFirstSync = !syncState || !syncState.lastUpdatedTime;
    let isInitialSync = isTrueFirstSync;
    if (syncState?.formType && syncState.formType !== formType) {
      // This org's MI form configuration appeared or disappeared since the last sync - full resync
      // so newly-available (or newly-unavailable) fields are captured consistently.
      isInitialSync = true;
    } else if (
      syncState?.formType === DynamicFormType.CONFIGUREDFORM &&
      syncState.lastFormModifiedOn &&
      formModifiedOn &&
      formModifiedOn > syncState.lastFormModifiedOn
    ) {
      // Same configured-form state, but the org's MI form definition was edited since the last
      // full sync (e.g. a field was added/removed) - full resync to reconcile.
      isInitialSync = true;
    }

    const fetchXml = this.buildFetchXmlWithDeltaSync(isInitialSync, syncState, attributesXml);

    try {
      const rawList = await this.dynamics.executeFetchQuery(MEDICAL_INQUIRY_ENTITY, fetchXml);
      const list = Array.isArray(rawList) ? rawList : [];

      if (isTrueFirstSync) {
        this.medicalInquiries = list.map((raw: Record<string, unknown>) => new MedicalInquiry(raw));
        await this.bulkSyncMyMiProducts();
      } else {
        if (this.medicalInquiries.length === 0) {
          await this.loadMedicalInquiriesFromDb();
        }
        const newList = list.map((raw: Record<string, unknown>) => new MedicalInquiry(raw));
        const existingMap = new Map(this.medicalInquiries.map(mi => [mi.id, mi]));
        for (const mi of newList) {
          const old = existingMap.get(mi.id);
          // Carry forward products from existing object so delta sync doesn't wipe filter data
          if (!mi.products?.length && old?.products?.length) mi.products = old.products;
          // Preserve extra fields fetched by all-attributes force-refresh (e.g. indskr_preferredmodeofcontact,
          // omp_expecteddeliverydate) that are absent from the delta sync template.
          // Delta sync fields take precedence as they carry the freshest server values.
          if (old?.rawData) {
            mi.rawData = { ...(old.rawData as Record<string, unknown>), ...(mi.rawData as Record<string, unknown>) };
          }
          existingMap.set(mi.id, mi);
        }
        // Existing entries not present in newList (e.g. offline-created drafts still pending
        // upload, or an MI whose create was deferred this cycle because its linked meeting
        // hadn't synced yet) are kept as-is by existingMap, not dropped - unlike a blind replace.
        this.medicalInquiries = Array.from(existingMap.values()).filter(mi => mi.isActive || mi.isCompleted || mi.isSubmitted);

        if (isInitialSync) {
          // Forced full refetch on a device that already has history (e.g. the org's MI form
          // config changed) - a blind product-store replace here could wipe pending offline
          // product rows for MIs not yet synced this cycle (see bulkSyncMyMiProducts). Only
          // replace product rows for MI ids the server actually told us about this cycle.
          const serverMiIds = new Set(
            list.map((raw: Record<string, unknown>) => raw?.['indskr_medicalinquiryid'] as string).filter(Boolean)
          );
          await this.bulkSyncMyMiProducts(serverMiIds);
        }
      }

      this.medicalInquiries.sort((a, b) => {
        const dateA = a.modifiedOn ? a.modifiedOn.getTime() : 0;
        const dateB = b.modifiedOn ? b.modifiedOn.getTime() : 0;
        return dateB - dateA;
      });

      this.medicalInquiries$.next(this.medicalInquiries);
      this.mergeIntoActivityService();
      await this.saveMedicalInquiriesToDb();
      const syncStateKey = DB_SYNC_STATE_KEYS.SYNC_MEDICAL_INQUIRIES;
      const updatedSyncState = syncState || { _id: syncStateKey };
      updatedSyncState.lastUpdatedTime = Date.now();
      updatedSyncState.formType = formType;
      if (formModifiedOn) updatedSyncState.lastFormModifiedOn = formModifiedOn;
      const useUpsert = !syncState || !syncState._id || !syncState._rev;
      await this.disk.updateSyncState(updatedSyncState, useUpsert);
    } catch (err: unknown) {
      this.logService.logError('MedicalInquiryDataService: Failed to fetch from server', err);
      await this.loadMedicalInquiriesFromDb();
    }
  }

  private buildFetchXmlWithDeltaSync(isInitialSync: boolean, syncState: MedicalInquirySyncState | null, attributesXml: string): string {
    const template = fetchQueries.medicalInquiry?.fetchMedicalInquiry;
    if (!template) return '';

    const deltaSyncFilter = isInitialSync
      ? ''
      : (() => {
          const lastSyncTime = syncState?.lastUpdatedTime ? new Date(syncState.lastUpdatedTime) : new Date();
          const hourDifference = Math.max(1, differenceInHours(new Date(), lastSyncTime) + 1);
          return `<condition attribute="modifiedon" operator="last-x-hours" value="${hourDifference}" />`;
        })();

    const ownerFilter = this.buildOwnerFilter();
    const stateFilter = `<filter type="or">
      <condition attribute='statecode' operator='eq' value='0' />
      <condition attribute='statuscode' operator='eq' value='${MedicalInquiryStatus.COMPLETED}' />
      <condition attribute='statuscode' operator='eq' value='${MedicalInquiryStatus.SUBMITTED}' />
    </filter>`;
    const medicalInquiryFilter = `${deltaSyncFilter}
      ${stateFilter}
      ${ownerFilter}`;

    return template.replace('{medicalInquiryAttributes}', attributesXml).replace('{medicalInquiryFilter}', medicalInquiryFilter);
  }

  private buildOwnerFilter(): string {
    return `<condition attribute='ownerid' operator='eq-userid' />`;
  }

  /**
   * Fetch team MI records (owned by child users) from Dynamics. Online-only.
   * Results are NOT persisted to PouchDB — stored in MedicalInquiryService.teamMedicalInquiries (memory only).
   */
  async fetchTeamMedicalInquiries(childUserIds: string[]): Promise<MedicalInquiry[]> {
    if (!childUserIds.length) return [];

    const template = fetchQueries.medicalInquiry?.fetchMedicalInquiry;
    if (!template) return [];

    const childUserValues = childUserIds.map(id => `<value>${id}</value>`).join('');
    const teamFilter = `<condition attribute='statecode' operator='eq' value='0' />
      <condition attribute='ownerid' operator='in'>${childUserValues}</condition>`;
    const { attributesXml } = this._resolveMedicalInquiryAttributes();
    const fetchXml = template.replace('{medicalInquiryAttributes}', attributesXml).replace('{medicalInquiryFilter}', teamFilter);

    try {
      const rawList = await this.dynamics.executeFetchQuery(MEDICAL_INQUIRY_ENTITY, fetchXml);
      const list = Array.isArray(rawList) ? rawList : [];
      return list.map((raw: Record<string, unknown>) => new MedicalInquiry(raw));
    } catch (err: unknown) {
      this.logService.logError('MedicalInquiryDataService: Failed to fetch team inquiries', err);
      return [];
    }
  }

  /**
   * Fetch MI records for agenda team view — same pattern as fetchTeamMedicalInquiries but scoped
   * to an agenda date window on indskr_reporteddate. startDate / endDate must be YYYY-MM-DD strings.
   * Results are NOT persisted; caller pushes them into agendaTeamViewActivities.
   */
  async fetchMedicalInquiriesForAgendaTeamView(
    childUserIds: string[],
    startDate: string,
    endDate: string
  ): Promise<MedicalInquiry[]> {
    if (!childUserIds.length) return [];

    const template = fetchQueries.medicalInquiry?.fetchMedicalInquiry;
    if (!template) return [];

    const childUserValues = childUserIds.map(id => `<value>${id}</value>`).join('');
    const teamFilter = `<condition attribute='statecode' operator='eq' value='0' />
      <condition attribute='ownerid' operator='in'>${childUserValues}</condition>
      <condition attribute='indskr_reporteddate' operator='on-or-after' value='${startDate}' />
      <condition attribute='indskr_reporteddate' operator='on-or-before' value='${endDate}' />`;
    const { attributesXml } = this._resolveMedicalInquiryAttributes();
    const fetchXml = template.replace('{medicalInquiryAttributes}', attributesXml).replace('{medicalInquiryFilter}', teamFilter);

    try {
      const rawList = await this.dynamics.executeFetchQuery(MEDICAL_INQUIRY_ENTITY, fetchXml);
      const list = Array.isArray(rawList) ? rawList : [];
      return list.map((raw: Record<string, unknown>) => new MedicalInquiry(raw));
    } catch (err: unknown) {
      this.logService.logError('MedicalInquiryDataService: Failed to fetch team MI for agenda', err);
      return [];
    }
  }

  private buildFetchXmlForMedicalInquiryById(id: string): string {
    const template = fetchQueries.medicalInquiry?.fetchMedicalInquiryById ?? fetchQueries.medicalInquiry?.fetchMedicalInquiry;
    if (!template) return '';
    const medicalInquiryFilter = `<condition attribute='indskr_medicalinquiryid' operator='eq' value='${id}'/>`;
    // fetchMedicalInquiryById uses <all-attributes/> and has no {medicalInquiryAttributes} token, so this
    // replace is a no-op there - only relevant if the ?? fallback above ever resolves to fetchMedicalInquiry.
    const { attributesXml } = this._resolveMedicalInquiryAttributes();
    return template.replace('{medicalInquiryAttributes}', attributesXml).replace('{medicalInquiryFilter}', medicalInquiryFilter);
  }

  /**
   * True only for PouchDB docs that are medical inquiry records (medical_inquiry_&lt;guid&gt;).
   * Excludes: linked-entity docs (including _id equal to bare prefix "medical_inquiry_"),
   * form option sets, and lookup-field docs that share the same key range.
   */
  private isMedicalInquiryRecordDoc(doc: { _id?: string }): boolean {
    const id = doc?._id ?? '';
    const prefix = DB_KEY_PREFIXES.MEDICAL_INQUIRY;
    if (!id.startsWith(prefix)) return false;
    if (id.startsWith(DB_KEY_PREFIXES.MEDICAL_INQUIRY_LINKED_ENTITY)) return false;
    if (id === DB_KEY_PREFIXES.MEDICAL_INQUIRY_FORM_OPTION_SETS) return false;
    if (id === DB_KEY_PREFIXES.MEDICAL_INQUIRY_FORM_LOOKUP_FIELDS) return false;
    // Exclude bare prefix key (e.g. _id "medical_inquiry_" used for linked-entity product blob)
    if (id.length <= prefix.length) return false;
    return true;
  }

  private async loadMedicalInquiriesFromDb(): Promise<void> {
    try {
      const opts = DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_MEDICAL_INQUIRIES;
      const docs: Array<{ _id?: string; raw?: Record<string, unknown> }> = await this.disk.batchFetch(opts, true, true) as any[];
      const inquiryDocs = (docs || []).filter(d => this.isMedicalInquiryRecordDoc(d));
      this.medicalInquiries = inquiryDocs
        .map(d => d?.raw)
        .filter(Boolean)
        .map((raw: Record<string, unknown>) => new MedicalInquiry(raw))
        .filter((mi: MedicalInquiry) => mi.isActive || mi.isCompleted || mi.isSubmitted);
      this.medicalInquiries.sort((a, b) => {
        const dateA = a.modifiedOn ? a.modifiedOn.getTime() : 0;
        const dateB = b.modifiedOn ? b.modifiedOn.getTime() : 0;
        return dateB - dateA;
      });
      this.medicalInquiries$.next(this.medicalInquiries);
      this.mergeIntoActivityService();
      await this.populateMiProductsFromDb();

      const pendingCount = inquiryDocs.filter(d => d?.raw && (d.raw as Record<string, unknown>).pendingSync === true).length;
      this.disk.setOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.MEDICAL_INQUIRY, pendingCount);
    } catch (err: unknown) {
      this.logService.logError('MedicalInquiryDataService: Failed to load from DB', err);
      this.medicalInquiries = [];
      this.medicalInquiries$.next([]);
    }
  }

  private async saveMedicalInquiriesToDb(): Promise<void> {
    for (const mi of this.medicalInquiries) {
      if (!mi?.id) continue;
      const dbKey = `${DB_KEY_PREFIXES.MEDICAL_INQUIRY}${mi.id}`;
      await this.disk.updateOrInsert(dbKey, () => ({ raw: mi.rawData || { ...mi } }));
    }
  }

  /**
   * Build nested products array for deep-insert (single POST with MI + products). Offline upload only.
   * Shape: indskr_medicalinquiryproduct_indskr_medicalinquiry_indskr_medicalinquiry: [ { "indskr_Product@odata.bind": "/products(id)" }, ... ]
   */
  private buildNestedProductsForDeepInsert(products: Array<Record<string, any>>): Array<Record<string, string>> {
    const out: Array<Record<string, string>> = [];
    for (const prod of products) {
      const productId = prod['indskr_medicalinquiryproduct.indskr_productid'] ?? prod['indskr_medicalinquiryproduct.indskr_product'];
      if (productId) {
        out.push({ 'indskr_Product@odata.bind': `/products(${productId})` });
      }
    }
    return out;
  }

  /**
   * Remove offline-created-and-scrapped MIs from memory and DB only (no API call).
   * Returns the updated product raw array (no in-place mutation).
   */
  private async discardOfflineCreatedScrappedLocally(
    offlineCreatedScrapped: Array<{ _id?: string; raw?: Record<string, any> }>,
    allProductRaw: any[],
    productDbKey: string
  ): Promise<any[]> {
    let currentRaw = allProductRaw;
    for (const item of offlineCreatedScrapped) {
      const localId = item.raw?.indskr_medicalinquiryid || item._id?.replace(DB_KEY_PREFIXES.MEDICAL_INQUIRY, '') || '';
      if (!localId) continue;
      this.medicalInquiries = this.medicalInquiries.filter(m => m.id !== localId);
      this.medicalInquiries$.next(this.medicalInquiries);
      await this.disk.remove(DB_KEY_PREFIXES.MEDICAL_INQUIRY + localId);
      const remaining = currentRaw.filter((r: any) => r?.indskr_medicalinquiryid !== localId);
      if (remaining.length !== currentRaw.length) {
        currentRaw = remaining;
        await this.disk.updateOrInsert(productDbKey, () => ({ raw: currentRaw }));
      }
      this.disk.subtractOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.MEDICAL_INQUIRY, 1);
    }
    return currentRaw;
  }

  /**
   * Pre-check pending creates: if a record already exists in Dynamics (e.g. retry after app kill),
   * treat as success and skip create. Returns the creates that still need to be sent and the updated product raw array (no in-place mutation).
   */
  private async filterCreatesToSendWithIdempotencyCheck(
    pendingCreates: Array<{ _id?: string; raw?: Record<string, any> }>,
    pendingProductsByLocalId: Map<string, any[]>,
    allProductRaw: any[],
    productDbKey: string
  ): Promise<{ createsToSend: Array<{ _id?: string; raw?: Record<string, any> }>; allProductRaw: any[] }> {
    const createsToSend: Array<{ _id?: string; raw?: Record<string, any> }> = [];
    let currentRaw = allProductRaw;
    for (const item of pendingCreates) {
      const localId = item.raw.indskr_medicalinquiryid || item._id?.replace(DB_KEY_PREFIXES.MEDICAL_INQUIRY, '') || '';
      try {
        const fetchXml = this.buildFetchXmlForMedicalInquiryById(localId);
        const rawList = await this.dynamics.executeFetchQuery(MEDICAL_INQUIRY_ENTITY, fetchXml);
        const existing = Array.isArray(rawList) && rawList.length > 0 ? rawList[0] : null;
        if (existing) {
          this.upsertMedicalInquiryFromApiResponse(existing);
          await this.disk.remove(DB_KEY_PREFIXES.MEDICAL_INQUIRY + localId);
          const productsToRemove = pendingProductsByLocalId.get(localId);
          if (productsToRemove?.length) {
            const remaining = currentRaw.filter((r: any) => r?.indskr_medicalinquiryid !== localId);
            currentRaw = remaining;
            await this.disk.updateOrInsert(productDbKey, () => ({ raw: currentRaw }));
          }
          this.disk.subtractOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.MEDICAL_INQUIRY, 1);
          continue;
        }
      } catch (err: unknown) {
        // Record does not exist in Dynamics (expected); proceed to create.
        this.logService.logDebug('MedicalInquiryDataService: pre-check query failed for', localId, err);
      }
      createsToSend.push(item);
    }
    return { createsToSend, allProductRaw: currentRaw };
  }

  /**
   * Add MI create requests to the current batch (and scrap-deactivate when pendingOperation is 'scrap').
   * Pushes entries to operationMap for later result processing.
   */
  private addMiCreatesToBatch(
    createsToSend: Array<{ _id?: string; raw?: Record<string, any> }>,
    pendingProductsByLocalId: Map<string, any[]>,
    operationMap: Array<{
      type: string;
      localId?: string;
      id?: string;
      productsIncluded?: any[];
      prod?: any;
      isScrapCreate?: boolean;
    }>
  ): void {
    for (const item of createsToSend) {
      const localId = item.raw.indskr_medicalinquiryid || item._id?.replace(DB_KEY_PREFIXES.MEDICAL_INQUIRY, '') || '';
      const payload = item.raw.pendingFormPayload ? this.mapFormPayloadToApiPayload(item.raw.pendingFormPayload) : {};
      (payload as any).indskr_medicalinquiryid = localId;
      // Re-derive the appointment bind from raw so subsequent offline edits/submits — which
      // call updateMedicalInquiryOffline and overwrite pendingFormPayload with the bind-less
      // form value — don't drop the meeting link. For offline meeting ids, resolveAppointmentRef
      // swaps in the now-synced real Dynamics GUID (MI upload is chained after meeting upload
      // in master.data.service, so by the time we get here the swap has happened).
      const apptRef = (item.raw['_indskr_appointment_value'] as string) || (item.raw['indskr_appointment'] as string) || '';
      const apptId = this.resolveAppointmentRef(apptRef);
      if (apptId
          && !(payload as any)['indskr_Appointment@odata.bind']
          && !(payload as any)['indskr_appointment@odata.bind']) {
        (payload as any)['indskr_Appointment@odata.bind'] = `/appointments(${apptId})`;
      }
      if (item.raw.pendingOperation === 'submit' && item.raw.pendingSignatureData) {
        (payload as any).indskr_hcpsignature = item.raw.pendingSignatureData.hcp;
        (payload as any).indskr_repsignature = item.raw.pendingSignatureData.rep;
        (payload as any).statuscode = MedicalInquiryStatus.SUBMITTED;
        if (item.raw.pendingSignatureData.hcpSignatureDate) {
          (payload as any).indskr_hcpsignaturedate = item.raw.pendingSignatureData.hcpSignatureDate;
        }
        if (item.raw.pendingSignatureData.repSignatureDate) {
          (payload as any).indskr_usersignaturedate = item.raw.pendingSignatureData.repSignatureDate;
        }
        if (item.raw.pendingSignatureData.hcpSignatureTerms) {
          (payload as any).indskr_hcpsignatureterms = item.raw.pendingSignatureData.hcpSignatureTerms;
        }
      }
      const isScrapCreate = item.raw.pendingOperation === 'scrap';
      const products = pendingProductsByLocalId.get(localId);
      let productsIncluded: any[] | undefined;
      if (products && products.length > 0) {
        const nested = this.buildNestedProductsForDeepInsert(products);
        if (nested.length > 0) {
          (payload as any).indskr_medicalinquiryproduct_indskr_medicalinquiry_indskr_medicalinquiry = nested;
          productsIncluded = products;
        }
      }
      operationMap.push({ type: 'create', localId, productsIncluded, isScrapCreate });
      this.dynamics.createRequest({
        collection: 'indskr_medicalinquiries',
        data: payload,
        returnRepresentation: true
      });
      if (isScrapCreate) {
        this.dynamics.update(localId, MEDICAL_INQUIRY_ENTITY, { statecode: 1, statuscode: 2 }, 'return=representation');
        operationMap.push({ type: 'scrap-deactivate', localId });
      }
    }
  }

  /**
   * Add MI update/scrap/submit requests to the current batch.
   * Pushes entries to operationMap for later result processing.
   */
  private addMiUpdatesScrapsSubmitsToBatch(
    pendingOthers: Array<{ _id?: string; raw?: Record<string, any> }>,
    operationMap: Array<{
      type: string;
      localId?: string;
      id?: string;
      productsIncluded?: any[];
      prod?: any;
      isScrapCreate?: boolean;
    }>
  ): void {
    for (const item of pendingOthers) {
      const id = item._id?.replace(DB_KEY_PREFIXES.MEDICAL_INQUIRY, '') || item.raw.indskr_medicalinquiryid || '';
      const op = item.raw.pendingOperation;
      if (op === 'update' && item.raw.pendingFormPayload) {
        const updatePayload = this.mapFormPayloadToApiPayload(item.raw.pendingFormPayload) as Record<string, unknown>;
        delete updatePayload.indskr_medicalinquiryid;
        this.dynamics.update(id, MEDICAL_INQUIRY_ENTITY, updatePayload, 'return=representation');
      } else if (op === 'scrap') {
        const scrapPayload: Record<string, unknown> = { statecode: 1, statuscode: MedicalInquiryStatus.INACTIVE };
        const offlineCreatedScrap = item.raw?.offlineCreatedAt != null;
        if (!offlineCreatedScrap) {
          scrapPayload['indskr_Appointment@odata.bind'] = null;
        }
        this.dynamics.update(id, MEDICAL_INQUIRY_ENTITY, scrapPayload, 'return=representation');
      } else if (op === 'submit' && item.raw.pendingSignatureData) {
        const submitData: Record<string, unknown> = {
          statuscode: MedicalInquiryStatus.SUBMITTED,
          indskr_hcpsignature: item.raw.pendingSignatureData.hcp,
          indskr_repsignature: item.raw.pendingSignatureData.rep,
        };
        if (item.raw.pendingSignatureData.hcpSignatureDate) {
          submitData.indskr_hcpsignaturedate = item.raw.pendingSignatureData.hcpSignatureDate;
        }
        if (item.raw.pendingSignatureData.repSignatureDate) {
          submitData.indskr_usersignaturedate = item.raw.pendingSignatureData.repSignatureDate;
        }
        if (item.raw.pendingSignatureData.hcpSignatureTerms) {
          submitData.indskr_hcpsignatureterms = item.raw.pendingSignatureData.hcpSignatureTerms;
        }
        this.dynamics.update(id, MEDICAL_INQUIRY_ENTITY, submitData, 'return=representation');
      }
      operationMap.push({ type: op, localId: id, id });
    }
  }

  /**
   * Add product deactivation requests to the current batch.
   * Pushes entries to operationMap for later result processing.
   */
  private addProductDeactivationsToBatch(
    pendingProductDeletes: any[],
    operationMap: Array<{
      type: string;
      localId?: string;
      id?: string;
      productsIncluded?: any[];
      prod?: any;
      isScrapCreate?: boolean;
    }>
  ): void {
    for (const prod of pendingProductDeletes) {
      const productRecordId = prod['indskr_medicalinquiryproduct.indskr_medicalinquiryproductid'];
      this.dynamics.update(
        productRecordId,
        MEDICAL_INQUIRY_PRODUCT_ENTITY,
        { statecode: 1, statuscode: MedicalInquiryStatus.INACTIVE },
        'return=representation'
      );
      operationMap.push({ type: 'product-delete', prod });
    }
  }

  /**
   * Add product create requests (for existing MIs) to the current batch.
   * Pushes entries to operationMap for later result processing.
   */
  private addProductCreatesToBatch(
    pendingProductCreates: any[],
    operationMap: Array<{
      type: string;
      localId?: string;
      id?: string;
      productsIncluded?: any[];
      prod?: any;
      isScrapCreate?: boolean;
    }>
  ): void {
    for (const prod of pendingProductCreates) {
      const inquiryId = prod.indskr_medicalinquiryid;
      const productId = prod['indskr_medicalinquiryproduct.indskr_productid'] ?? prod['indskr_medicalinquiryproduct.indskr_product'];
      if (!productId) continue;
      const valuesWithLookup = {
        lookupfields: [
          { name: 'indskr_medicalinquiry', entity: 'indskr_medicalinquiries', id: inquiryId },
          { name: 'indskr_product', entity: 'products', id: productId }
        ]
      };
      const payload = this.mapMedicalInquiryProductForCreateLinkedEntity(valuesWithLookup, inquiryId);
      if (payload && Object.keys(payload).length) {
        (payload as any).statecode = 0;
        (payload as any).statuscode = MedicalInquiryStatus.DRAFT;
        this.dynamics.createRequest({
          collection: MEDICAL_INQUIRY_PRODUCT_ENTITY,
          data: payload,
          returnRepresentation: true
        });
        operationMap.push({ type: 'product-create', prod });
      }
    }
  }

  /**
   * Apply batch results to local state: update in-memory MIs, remove disk docs, update product store, persist and set offline count.
   */
  private async applyBatchResultsToLocalState(
    operationMap: Array<{
      type: string;
      localId?: string;
      id?: string;
      productsIncluded?: any[];
      prod?: any;
      isScrapCreate?: boolean;
    }>,
    batchResults: any[],
    allProductRaw: any[],
    productDbKey: string
  ): Promise<void> {
    const productsToRemoveFromStore: any[] = [];

    for (let i = 0; i < operationMap.length; i++) {
      const entry = operationMap[i];
      const result = Array.isArray(batchResults) ? batchResults[i] : null;
      if (entry.type === 'scrap-deactivate') continue;

      if (entry.type === 'create') {
        const realId = result?.indskr_medicalinquiryid;
        if (realId) {
          this.medicalInquiries = this.medicalInquiries.filter(m => m.id !== entry.localId);
          if (!entry.isScrapCreate) {
            this.upsertMedicalInquiryFromApiResponse(result);
          }
          await this.disk.remove(DB_KEY_PREFIXES.MEDICAL_INQUIRY + entry.localId);
          if (entry.productsIncluded?.length) {
            productsToRemoveFromStore.push(...entry.productsIncluded);
          }
        } else {
          this.logService.logError('MedicalInquiryDataService: create in batch failed for', entry.localId, result);
        }
      } else if (entry.type === 'update' && result) {
        this.upsertMedicalInquiryFromApiResponse(result);
      } else if (entry.type === 'scrap' && result) {
        this.medicalInquiries = this.medicalInquiries.filter(m => m.id !== entry.id);
        this.medicalInquiries$.next(this.medicalInquiries);
        await this.disk.remove(DB_KEY_PREFIXES.MEDICAL_INQUIRY + entry.id);
      } else if (entry.type === 'submit' && result) {
        this.upsertMedicalInquiryFromApiResponse(result);
      } else if (entry.type === 'product-delete' && result) {
        productsToRemoveFromStore.push(entry.prod);
      } else if (entry.type === 'product-create' && result) {
        productsToRemoveFromStore.push(entry.prod);
      }
    }

    await this.saveMedicalInquiriesToDb();

    if (productsToRemoveFromStore.length > 0) {
      const toRemove = new Set(productsToRemoveFromStore);
      const remaining = allProductRaw.filter((r: any) => !toRemove.has(r));
      await this.disk.updateOrInsert(productDbKey, () => ({ raw: remaining }));
    }

    this.disk.setOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.MEDICAL_INQUIRY, 0);
  }

  /**
   * Push all pending offline changes to Dynamics in a single batch request.
   * Handles MI creates (with deep insert for offline products), MI updates/scraps/submits,
   * product deactivations, and product creates for existing MIs — all in one round trip.
   */
  async uploadPendingMedicalInquiries(): Promise<void> {
    if (this.deviceService.isOffline) return;

    try {
      const opts = DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_MEDICAL_INQUIRIES;
      const docs: Array<{ _id?: string; raw?: Record<string, any> }> = (await this.disk.batchFetch(opts, true, true)) as any[];
      const inquiryDocs = (docs || []).filter(d => this.isMedicalInquiryRecordDoc(d));
      const pending = inquiryDocs.filter(d => d?.raw && d.raw.pendingSync === true);

      // Load the linked entity product store once for all product operations
      const productDbKey = `${DB_KEY_PREFIXES.MEDICAL_INQUIRY_LINKED_ENTITY}indskr_medicalinquiryproduct`;
      const productDoc = await this.disk.retrieve(productDbKey);
      let allProductRaw: any[] = Array.isArray(productDoc?.raw) ? productDoc.raw : [];

      // Offline-created MIs: have offlineCreatedAt and must be created (not updated) — they don't exist in Dynamics yet.
      // Offline-created + scrapped: never synced to Dynamics; discard locally only (no API call).
      const isOfflineCreated = (p: { raw?: Record<string, any> }) => !!p.raw?.offlineCreatedAt;
      const pendingCreates = pending
        .filter(p =>
          p.raw.pendingOperation === 'create' ||
          (isOfflineCreated(p) && p.raw.pendingOperation === 'submit')
        )
        .sort((a, b) => (a.raw.offlineCreatedAt || 0) - (b.raw.offlineCreatedAt || 0));

      // Defer MI creates whose offline-meeting reference can't be resolved this cycle (the
      // meeting hasn't synced yet — partial upload, meeting-upload failure, etc.). Deferred
      // records remain in PouchDB with pendingSync=true and retry on the next sync. This
      // guarantees an MI is never present-but-unlinked in Dynamics: it either has the bind
      // or it isn't there yet.
      const syncableCreates: typeof pendingCreates = [];
      const deferredCreates: typeof pendingCreates = [];
      for (const p of pendingCreates) {
        const apptRef = (p.raw?.['_indskr_appointment_value'] as string) || '';
        if (apptRef && apptRef.includes('offline') && !this.resolveAppointmentRef(apptRef)) {
          deferredCreates.push(p);
        } else {
          syncableCreates.push(p);
        }
      }
      if (deferredCreates.length > 0) {
        this.logService.logWarning(
          `MedicalInquiryDataService: deferring ${deferredCreates.length} MI create(s) — offline meeting not yet synced`
        );
      }

      const pendingOthers = pending.filter(
        p => !isOfflineCreated(p) && (p.raw.pendingOperation === 'update' || p.raw.pendingOperation === 'scrap' || p.raw.pendingOperation === 'submit')
      );

      // Offline-created and scrapped offline: remove from local storage only; do not send to Dynamics.
      const offlineCreatedScrapped = pending.filter(
        p => isOfflineCreated(p) && p.raw.pendingOperation === 'scrap'
      );
      allProductRaw = await this.discardOfflineCreatedScrappedLocally(offlineCreatedScrapped, allProductRaw, productDbKey);

      const pendingCreateIds = new Set(pendingCreates.map(p => p.raw?.indskr_medicalinquiryid || p._id?.replace(DB_KEY_PREFIXES.MEDICAL_INQUIRY, '') || ''));

      // Products for offline-created MIs (used for deep insert in MI create payload)
      const pendingProductsByLocalId = new Map<string, any[]>();
      for (const r of allProductRaw) {
        const inquiryId = r?.indskr_medicalinquiryid;
        if (inquiryId && pendingCreateIds.has(inquiryId)) {
          if (!pendingProductsByLocalId.has(inquiryId)) pendingProductsByLocalId.set(inquiryId, []);
          pendingProductsByLocalId.get(inquiryId).push(r);
        }
      }

      // Product deactivations (real product record IDs, not offline_product_ placeholders)
      const pendingProductDeletes = allProductRaw.filter((r: any) => {
        const productId = r?.['indskr_medicalinquiryproduct.indskr_medicalinquiryproductid'];
        return r?.pendingDelete === true && productId && !String(productId).startsWith('offline_');
      });

      // Offline-added-then-deleted products: remove from store only (they were never created in Dynamics)
      const offlineDeletedProducts = allProductRaw.filter((r: any) => {
        const productId = r?.['indskr_medicalinquiryproduct.indskr_medicalinquiryproductid'];
        return r?.pendingDelete === true && productId && String(productId).startsWith('offline_');
      });
      if (offlineDeletedProducts.length > 0) {
        const toDiscard = new Set(offlineDeletedProducts);
        allProductRaw = allProductRaw.filter((r: any) => !toDiscard.has(r));
        await this.disk.updateOrInsert(productDbKey, () => ({ raw: allProductRaw }));
      }

      // New product creates for existing (server-synced) MIs — exclude deleted and pending-create MI products
      const pendingProductCreates = allProductRaw.filter((r: any) => {
        const inquiryId = r?.indskr_medicalinquiryid;
        return inquiryId && r.pendingSync === true && !r.pendingDelete && !pendingCreateIds.has(inquiryId);
      });

      if (!syncableCreates.length && !pendingOthers.length && !pendingProductDeletes.length && !pendingProductCreates.length) return;

      const { createsToSend, allProductRaw: allProductRawAfterFilter } = await this.filterCreatesToSendWithIdempotencyCheck(
        syncableCreates,
        pendingProductsByLocalId,
        allProductRaw,
        productDbKey
      );
      allProductRaw = allProductRawAfterFilter;

      const operationMap: Array<{
        type: string;
        localId?: string;
        id?: string;
        productsIncluded?: any[];
        prod?: any;
        isScrapCreate?: boolean;
      }> = [];
      // Pass includeAnnotations so batch responses carry @OData.Community.Display.V1.FormattedValue
      // keys (picklists, lookup display names, owner names, etc.).  Without this the default
      // config-less batch DWA instance strips annotations, leaving statuscode, contactFormatted,
      // caseTypeFormatted, reportTypeFormatted, etc. empty after sync (Obs 4 — OMNI-59455).
      this.dynamics.startBatch({ returnRepresentation: true, includeAnnotations: '*' });

      this.addMiCreatesToBatch(createsToSend, pendingProductsByLocalId, operationMap);

      this.addMiUpdatesScrapsSubmitsToBatch(pendingOthers, operationMap);

      this.addProductDeactivationsToBatch(pendingProductDeletes, operationMap);

      this.addProductCreatesToBatch(pendingProductCreates, operationMap);

      const batchResults: any[] = await this.dynamics.executeBatch();

      await this.applyBatchResultsToLocalState(operationMap, batchResults, allProductRaw, productDbKey);

      // applyBatchResultsToLocalState resets the offline count to 0 assuming a clean sync.
      // Deferred MIs (offline meeting not yet synced) are still pending, so restore the count.
      if (deferredCreates.length > 0) {
        this.disk.setOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.MEDICAL_INQUIRY, deferredCreates.length);
      }
    } catch (err: unknown) {
      this.logService.logError('MedicalInquiryDataService: uploadPendingMedicalInquiries failed', err);
    }
  }

  /**
   * Fetch and sync medical inquiry products for a given MI. When online, fetches from Dynamics,
   * merges into local linked entity storage. When offline, products come from local DB (set by addProductAfterCreation / addProductOffline).
   * Used by display form to show products in subgrid.
   */
  async syncMedicalInquiryProductsForDisplay(medicalInquiryId: string): Promise<Record<string, any>[]> {
    const existing = this._syncProductsInFlight.get(medicalInquiryId);
    if (existing) return existing;

    const promise = this._doSyncMedicalInquiryProductsForDisplay(medicalInquiryId);
    this._syncProductsInFlight.set(medicalInquiryId, promise);
    promise.finally(() => this._syncProductsInFlight.delete(medicalInquiryId));
    return promise;
  }

  private async _doSyncMedicalInquiryProductsForDisplay(medicalInquiryId: string): Promise<Record<string, any>[]> {
    const dbKey = `${DB_KEY_PREFIXES.MEDICAL_INQUIRY_LINKED_ENTITY}indskr_medicalinquiryproduct`;

    if (this.deviceService.isOffline) {
      const doc = await this.disk.retrieve(dbKey);
      const raw = Array.isArray(doc?.raw) ? (doc.raw as Array<Record<string, unknown>>) : [];
      const filtered = this._filterProductsByInquiryAndActive(raw, medicalInquiryId);
      return this._enrichOfflineProductsWithDisplayNames(filtered as Record<string, any>[]);
    }

    try {
      const fetchXml = fetchQueries.medicalInquiry?.fetchMedicalInquiryProductsByInquiryId?.replace(
        '{medicalInquiryId}',
        medicalInquiryId
      );
      if (!fetchXml) return [];

      const result = await this.dynamics.executeFetchQuery(MEDICAL_INQUIRY_PRODUCT_ENTITY, fetchXml);
      const rows = Array.isArray(result) ? result : [];
      const normalized = rows.map((r: any) => this._normalizeProductRecordForDisplay(r));

      const existing = await this.disk.retrieve(dbKey);
      const localRaw = Array.isArray(existing?.raw) ? existing.raw : [];
      const others = localRaw.filter((r: any) => r?.indskr_medicalinquiryid !== medicalInquiryId);
      const merged = [...others, ...normalized];
      await this.disk.updateOrInsert(dbKey, () => ({ raw: merged }));

      // Update mi.products on the in-memory My MI so the list filter stays current after detail open
      const myMi = this.medicalInquiries.find(m => m.id === medicalInquiryId);
      if (myMi) {
        myMi.products = normalized
          .map((r: any) => ({
            id: r['indskr_medicalinquiryproduct.indskr_product']
              ?? r['indskr_medicalinquiryproduct.indskr_productid'] ?? '',
            name: r['indskr_medicalinquiryproduct.indskr_product@OData.Community.Display.V1.FormattedValue'] ?? '',
          }))
          .filter((p: { id: string; name: string }) => p.id);
      }

      return normalized;
    } catch (err: unknown) {
      this.logService.logError('MedicalInquiryDataService: syncMedicalInquiryProductsForDisplay failed', err);
      const doc = await this.disk.retrieve(dbKey);
      const raw = Array.isArray(doc?.raw) ? (doc.raw as Array<Record<string, unknown>>) : [];
      const filtered = this._filterProductsByInquiryAndActive(raw, medicalInquiryId);
      return this._enrichOfflineProductsWithDisplayNames(filtered as Record<string, any>[]);
    }
  }

  /** Filter product records by medical inquiry id and active state (statecode 0 or missing). */
  private _filterProductsByInquiryAndActive(
    raw: Array<Record<string, unknown>>,
    medicalInquiryId: string
  ): Array<Record<string, unknown>> {
    return raw.filter(
      r =>
        r?.indskr_medicalinquiryid === medicalInquiryId &&
        (r['indskr_medicalinquiryproduct.statecode'] === undefined || r['indskr_medicalinquiryproduct.statecode'] === 0)
    );
  }

  /** Enrich offline product records with display names when FormattedValue is missing (e.g. records added before fix). */
  private _enrichOfflineProductsWithDisplayNames(products: Record<string, any>[]): Record<string, any>[] {
    const formattedKey = 'indskr_medicalinquiryproduct.indskr_product@OData.Community.Display.V1.FormattedValue';
    return products.map((r: any) => {
      if (r[formattedKey]) return r;
      const productId = r['indskr_medicalinquiryproduct.indskr_product'] ?? r['indskr_medicalinquiryproduct.indskr_productid'];
      const name = this._resolveProductNameFromLookupCache(productId);
      if (name) return { ...r, [formattedKey]: name };
      return r;
    });
  }

  /** Resolve product name from MI form lookup cache when offline (fallback when formStringValue not passed). */
  private _resolveProductNameFromLookupCache(productId: string): string {
    if (!productId) return '';
    const miCache = this.dynamicFormsService.getMIFormLookupFields();
    const productCache = miCache?.['indskr_product'] ?? miCache?.['indskr_Product'];
    if (Array.isArray(productCache)) {
      const found = productCache.find((p: any) => p?.id === productId);
      if (found?.name) return String(found.name);
    }
    const offlineEditCache = this.dynamicFormsService.allLookUpFieldsForOfflineEdit;
    const offlineProducts = offlineEditCache?.['indskr_product'] ?? offlineEditCache?.['indskr_Product'];
    if (Array.isArray(offlineProducts)) {
      const found = offlineProducts.find((p: any) => p?.id === productId);
      if (found?.name) return String(found.name);
    }
    return '';
  }

  /**
   * If the Dynamics title-generation plugin has not yet run (title has no "ProductName - " prefix)
   * but a product is linked via _omp_product_value, construct the title locally so the
   * list and detail header show the correct value immediately. The server-generated title
   * overwrites this on the next force-refresh after submit.
   */
  private _patchTitleFromProduct(mi: MedicalInquiry): void {
    if (mi.title?.includes(' - ')) return;
    const rd = mi.rawData as Record<string, unknown>;
    const productName = rd['_omp_product_value@OData.Community.Display.V1.FormattedValue'] as string | undefined;
    const indskrId = rd['indskr_id'] as string | undefined;
    if (productName && indskrId) {
      const compositeTitle = `${productName} - ${indskrId}`;
      mi.title = compositeTitle;
      rd['indskr_title'] = compositeTitle;
    }
  }

  private _normalizeProductRecordForDisplay(raw: Record<string, any>): Record<string, any> {
    const prefix = 'indskr_medicalinquiryproduct.';
    const out: Record<string, any> = {};
    // Dynamics FetchXML response uses _indskr_medicalinquiry_value, _indskr_product_value for lookups
    const inquiryId = raw._indskr_medicalinquiry_value;
    const productId = raw._indskr_product_value;
    const rootAttrs = ['indskr_medicalinquiryproductid', 'statecode', 'statuscode'];
    for (const k of rootAttrs) {
      if (raw[k] !== undefined) out[prefix + k] = raw[k];
    }
    if (inquiryId !== undefined) out[prefix + 'indskr_medicalinquiryid'] = inquiryId;
    out['indskr_medicalinquiryid'] = inquiryId;
    if (productId !== undefined) {
      out[prefix + 'indskr_productid'] = productId;
      out['indskr_medicalinquiryproduct.indskr_product'] = productId;
      out['indskr_medicalinquiryproduct.indskr_productid'] = productId;
    }
    if (raw['product.name']) out[prefix + 'indskr_product@OData.Community.Display.V1.FormattedValue'] = raw['product.name'];
    return out;
  }

  /**
   * Get a single medical inquiry by ID.
   * @param forceRefresh When true, fetches from API/disk and updates cache (use after update to avoid stale display).
   */
  async getMedicalInquiryById(id: string, forceRefresh = false): Promise<MedicalInquiry | null> {
    if (!id) return null;
    if (!forceRefresh) {
      const fromMemory = this.medicalInquiries.find(mi => mi.id === id);
      if (fromMemory) return fromMemory;
    }

    if (this.deviceService.isOffline) {
      const dbKey = `${DB_KEY_PREFIXES.MEDICAL_INQUIRY}${id}`;
      const doc = await this.disk.retrieve(dbKey);
      if (!doc?.raw) return null;
      const baseRaw = doc.raw as Record<string, unknown>;
      const raw = await this.enrichMedicalInquiryRawDataForDisplay(baseRaw);
      const mi = new MedicalInquiry(raw);
      if (forceRefresh) {
        this.upsertMedicalInquiry(mi);
        this._syncRefreshedMiToAgenda(mi);
      }
      return mi;
    }

    try {
      const fetchXml = this.buildFetchXmlForMedicalInquiryById(id);
      if (!fetchXml) return null;
      const rawList = await this.dynamics.executeFetchQuery(MEDICAL_INQUIRY_ENTITY, fetchXml);
      const list = Array.isArray(rawList) ? rawList : [];
      if (list.length > 0) {
        const mi = new MedicalInquiry(list[0]);
        this._patchTitleFromProduct(mi);
        await this.disk.updateOrInsert(`${DB_KEY_PREFIXES.MEDICAL_INQUIRY}${id}`, () => ({ raw: mi.rawData }));
        if (forceRefresh) {
          this.upsertMedicalInquiry(mi);
          this._syncRefreshedMiToAgenda(mi);
        }
        return mi;
      }
    } catch (err: unknown) {
      this.logService.logError('MedicalInquiryDataService: Failed to get by ID', err);
    }
    return null;
  }

  /**
   * Enrich rawData with FormattedValue for optionsets and lookups (including contact) for display/edit when offline.
   * Call this when building display data so ind-display-form shows labels. Does not mutate input; returns new object.
   */
  async enrichMedicalInquiryRawDataForDisplay(rawData: Record<string, unknown>): Promise<Record<string, unknown>> {
    const enriched = await this.dynamicFormsService.enrichMedicalInquiryRawDataForDisplay(rawData ?? {});
    this._resolveContactLookupForDisplay(enriched);
    await this._resolveAddressLookupForDisplay(enriched);
    return enriched;
  }

  /**
   * When offline, contact lookup is not in MI lookup cache (contacts are in SKIP_ENTITIES).
   * Resolve _indskr_contact_value to display name from contact offline cache for display form.
   */
  private _resolveContactLookupForDisplay(raw: Record<string, unknown>): void {
    const contactId = raw['_indskr_contact_value'] as string | undefined;
    const formattedKey = '_indskr_contact_value@OData.Community.Display.V1.FormattedValue';
    if (contactId && (raw[formattedKey] === undefined || raw[formattedKey] === '')) {
      const name = this.contactOfflineService.getFullNameByContactId(contactId);
      if (name) raw[formattedKey] = name;
    }
  }

  /**
   * When offline, address chosen from local contact addresses may not be in the MI form lookup
   * cache (prefetched via generic fetchXml). Resolve the composite address text by reusing
   * getAddressesForContactFromLocal (memory-first, then Pouch filtered by contactId).
   */
  private async _resolveAddressLookupForDisplay(raw: Record<string, unknown>): Promise<void> {
    const addressId = raw['_indskr_address_value'] as string | undefined;
    const formattedKey = '_indskr_address_value@OData.Community.Display.V1.FormattedValue';
    if (!addressId || (raw[formattedKey] !== undefined && raw[formattedKey] !== '')) return;

    const contactId = raw['_indskr_contact_value'] as string | undefined;
    if (!contactId) return;

    const addresses = await this.contactOfflineService.getAddressesForContactFromLocal(contactId);
    const match = addresses.find(a => a.addressId === addressId);
    if (match?.compositeAdd) raw[formattedKey] = match.compositeAdd;
  }

  /**
   * Save a product for an offline-created MI to local linked entity store. Pushed to API when MI is synced (Batch 2).
   * Stores product FormattedValue for display when offline (product name instead of ID).
   */
  async addProductOffline(
    medicalInquiryId: string,
    productPayload: Record<string, any>,
    productFormStringValue?: Record<string, string>
  ): Promise<string> {
    const productLf = productPayload?.lookupfields?.find((lf: any) => lf.name === 'indskr_product' || lf.name === 'indskr_Product');
    const productId = productLf?.id ?? '';
    const productName =
      productFormStringValue?.['indskr_product'] ??
      productFormStringValue?.['indskr_Product'] ??
      this._resolveProductNameFromLookupCache(productId);

    const tempProductId = 'offline_product_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7);
    const productRecord: Record<string, any> = {
      indskr_medicalinquiryid: medicalInquiryId,
      'indskr_medicalinquiryproduct.indskr_medicalinquiryproductid': tempProductId,
      'indskr_medicalinquiryproduct.indskr_product': productId,
      'indskr_medicalinquiryproduct.indskr_productid': productId,
      'indskr_medicalinquiryproduct.statecode': 0,
      pendingSync: true,
    };
    if (productName) {
      productRecord['indskr_medicalinquiryproduct.indskr_product@OData.Community.Display.V1.FormattedValue'] = productName;
    }

    const dbKey = `${DB_KEY_PREFIXES.MEDICAL_INQUIRY_LINKED_ENTITY}indskr_medicalinquiryproduct`;
    const existing = await this.disk.retrieve(dbKey);
    const raw = Array.isArray(existing?.raw) ? [...existing.raw] : [];
    raw.push(productRecord);
    await this.disk.updateOrInsert(dbKey, () => ({ raw }));

    // Update mi.products so the list filter reflects the new product immediately
    if (productId) {
      const mi = this.medicalInquiries.find(m => m.id === medicalInquiryId);
      if (mi) {
        mi.products = [...(mi.products ?? []), { id: productId, name: productName ?? '' }];
      }
    }

    return tempProductId;
  }

  /**
   * After successful product creation (online only): append product with GUID to linked entity storage,
   * and update MI in memory. Offline adds go through addProductOffline.
   * No server calls here: the batch caller refetches the MI, and its products when a name was not resolved.
   * @param medicalInquiryId MI id
   * @param productPayload Original form values (lookupfields, etc.)
   * @param createResponse API response with indskr_medicalinquiryproductid
   * @returns Whether the product's display name was resolved locally
   */
  async addProductAfterCreation(
    medicalInquiryId: string,
    productPayload: Record<string, any>,
    createResponse: Record<string, any>,
    productFormStringValue?: Record<string, string>
  ): Promise<boolean> {
    const guid = createResponse?.indskr_medicalinquiryproductid ?? createResponse?.id ?? createResponse?.guid;
    if (!guid) return true;

    const productLf = productPayload?.lookupfields?.find((lf: any) => lf.name === 'indskr_product' || lf.name === 'indskr_Product');
    const productId = productLf?.id ?? '';
    const productRecord: Record<string, any> = {
      indskr_medicalinquiryid: medicalInquiryId,
      'indskr_medicalinquiryproduct.indskr_medicalinquiryproductid': guid,
      'indskr_medicalinquiryproduct.indskr_product': productId,
      'indskr_medicalinquiryproduct.indskr_productid': productId,
      'indskr_medicalinquiryproduct.statecode': 0
    };
    const productName =
      productFormStringValue?.['indskr_product'] ||
      productFormStringValue?.['indskr_Product'] ||
      this._resolveProductNameFromLookupCache(productId);
    if (productName) productRecord['indskr_medicalinquiryproduct.indskr_product@OData.Community.Display.V1.FormattedValue'] = productName;

    const dbKey = `${DB_KEY_PREFIXES.MEDICAL_INQUIRY_LINKED_ENTITY}indskr_medicalinquiryproduct`;
    const existing = await this.disk.retrieve(dbKey);
    const raw = Array.isArray(existing?.raw) ? [...existing.raw] : [];
    raw.push(productRecord);
    await this.disk.updateOrInsert(dbKey, () => ({ raw }));

    // Update mi.products so the list filter reflects the new product immediately
    if (productId) {
      const inMemoryMi = this.medicalInquiries.find(m => m.id === medicalInquiryId);
      if (inMemoryMi && !inMemoryMi.products?.some(p => p.id === productId)) {
        inMemoryMi.products = [...(inMemoryMi.products ?? []), { id: productId, name: productName ?? '' }];
      }
    }
    return !!productName;
  }

  /**
   * Mark an existing product as deleted offline. Updates the linked entity store so the delete is synced to Dynamics on reconnect.
   * Only applies to products that already exist in Dynamics (have a real indskr_medicalinquiryproductid).
   */
  async markProductDeletedOffline(medicalInquiryId: string, productRecordId: string): Promise<void> {
    const dbKey = `${DB_KEY_PREFIXES.MEDICAL_INQUIRY_LINKED_ENTITY}indskr_medicalinquiryproduct`;
    const doc = await this.disk.retrieve(dbKey);
    const raw = Array.isArray(doc?.raw) ? [...doc.raw] : [];
    const idx = raw.findIndex(
      (r: any) =>
        r?.indskr_medicalinquiryid === medicalInquiryId &&
        r['indskr_medicalinquiryproduct.indskr_medicalinquiryproductid'] === productRecordId
    );
    if (idx < 0) return;

    // Remove from mi.products before writing so the list filter reflects the deletion immediately
    const deletedProductId: string = raw[idx]['indskr_medicalinquiryproduct.indskr_product']
      ?? raw[idx]['indskr_medicalinquiryproduct.indskr_productid'];
    if (deletedProductId) {
      const mi = this.medicalInquiries.find(m => m.id === medicalInquiryId);
      if (mi) {
        mi.products = (mi.products ?? []).filter(p => p.id !== deletedProductId);
      }
    }

    raw[idx]['indskr_medicalinquiryproduct.statecode'] = 1;
    raw[idx].pendingDelete = true;
    await this.disk.updateOrInsert(dbKey, () => ({ raw }));
  }

  /**
   * Create a medical inquiry offline. Uses a frontend-generated GUID as the ID; record is queued for push on reconnect.
   * GUID enables deterministic idempotency: if create succeeds but app never receives response, retry uses same GUID
   * and Dynamics will reject duplicate (record exists) or we pre-check existence before create.
   *
   * @param formStringValue Optional display-name map from `ind-dynamic-form` (`data.formStringValue`).
   * Used to enrich the read-side OData lookup keys on `rawData` so derived model fields like
   * `contactFormatted` are populated immediately. Without it, offline-created MIs would show empty
   * Customer on the LHS card / agenda and an empty "Signed By" line in the capture modal at submit
   * time, because the form payload only carries write-side keys (`lookupfields`, `*@odata.bind`)
   * and the `MedicalInquiry` constructor reads `_indskr_contact_value@OData.Community.Display.V1.FormattedValue`.
   */
  createMedicalInquiryOffline(
    formPayload: Record<string, unknown>,
    formStringValue?: Record<string, string>
  ): MedicalInquiry {
    const localId = Guid.create().toString();
    const createdAt = Date.now();
    const rawData: Record<string, unknown> = {
      ...formPayload,
      indskr_medicalinquiryid: localId,
      // Set owner so the record appears under "My" tab while pending sync.
      // Without this, ownerId is empty and the record leaks into the Team tab.
      _ownerid_value: this.authService.user?.systemUserID || '',
      statecode: 0,
      statuscode: MedicalInquiryStatus.DRAFT,
      pendingSync: true,
      pendingOperation: 'create',
      pendingFormPayload: formPayload,
      offlineCreatedAt: createdAt,
      createdon: new Date(createdAt).toISOString(),
    };
    this._enrichOfflineRawWithLookupReadSideKeys(rawData, formPayload, formStringValue);
    const mi = new MedicalInquiry(rawData);
    this.upsertMedicalInquiry(mi);
    this.disk.addOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.MEDICAL_INQUIRY, 1);
    return mi;
  }

  /**
   * Maps write-side lookups (`lookupfields` / `*@odata.bind`) to read-side `_<attr>_value` + OData
   * formatted-value keys so `MedicalInquiry` gets labels offline (vs API-shaped responses).
   * Display names from `formStringValue`; contact falls back to `contactOfflineService.getFullNameByContactId`.
   * OMNI-59455 / Obs 3 sibling: empty Signed By / LHS customer when MI created offline without these keys.
   */
  private _enrichOfflineRawWithLookupReadSideKeys(
    raw: Record<string, unknown>,
    formPayload: Record<string, unknown>,
    formStringValue?: Record<string, string>
  ): void {
    const seenAttrs = new Set<string>();

    const lookupfields = formPayload['lookupfields'];
    if (Array.isArray(lookupfields)) {
      for (const lf of lookupfields as Array<{ name?: string; id?: string }>) {
        const name = lf?.name;
        const idRaw = lf?.id;
        if (!name || !idRaw) continue;
        const id = String(idRaw).trim();
        if (!id) continue;
        const attrLower = name.toLowerCase();
        if (seenAttrs.has(attrLower)) continue;
        this._mirrorLookupKey(raw, attrLower, name, id, formStringValue);
        seenAttrs.add(attrLower);
      }
    }

    for (const key of Object.keys(formPayload)) {
      if (!key.endsWith('@odata.bind')) continue;
      const attrOriginal = key.slice(0, -'@odata.bind'.length);
      const attrLower = attrOriginal.toLowerCase();
      if (seenAttrs.has(attrLower)) continue;
      const id = this._extractGuidFromODataBind(formPayload[key]);
      if (!id) continue;
      this._mirrorLookupKey(raw, attrLower, attrOriginal, id, formStringValue);
      seenAttrs.add(attrLower);
    }
  }

  /**
   * Write a single `_<attr>_value` (and its formatted-value companion) on `raw`, preserving any
   * pre-existing formatted value (don't overwrite values sourced from a Dynamics API response).
   * For the contact lookup, falls back to the offline contact cache when the form didn't pass
   * a display name.
   */
  private _mirrorLookupKey(
    raw: Record<string, unknown>,
    attrLower: string,
    attrOriginal: string,
    id: string,
    formStringValue?: Record<string, string>
  ): void {
    const valueKey = `_${attrLower}_value`;
    const formattedKey = `${valueKey}@OData.Community.Display.V1.FormattedValue`;

    raw[valueKey] = id;

    if (raw[formattedKey] != null && raw[formattedKey] !== '') return;

    let displayName =
      (formStringValue?.[attrOriginal] as string | undefined)
      ?? (formStringValue?.[attrLower] as string | undefined)
      ?? '';
    if (!displayName && attrLower === 'indskr_contact') {
      displayName = this.contactOfflineService.getFullNameByContactId(id) || '';
    }
    if (displayName) raw[formattedKey] = displayName;
  }

  /** Parse `'/contacts(<guid>)'` (or any `'/<set>(<guid>)'`) into the GUID, or empty string. */
  private _extractGuidFromODataBind(bindValue: unknown): string {
    if (typeof bindValue !== 'string') return '';
    const match = bindValue.match(/\(([^)]+)\)/);
    return match?.[1]?.trim() ?? '';
  }

  /**
   * Shared helper: find MI by id, apply pending fields via callback, refresh derived fields by
   * reconstructing the MedicalInquiry from the updated rawData, persist, and notify subscribers.
   * Products are preserved by `upsertMedicalInquiry`'s carry-forward guard.
   * @returns true if MI was found and updated
   */
  private _applyOfflinePendingChange(
    id: string,
    applyFields: (raw: Record<string, unknown>) => void
  ): boolean {
    const existing = this.medicalInquiries.find(m => m.id === id);
    if (!existing) return false;
    const raw = (existing.rawData || {}) as Record<string, unknown>;
    const wasAlreadyPending = (raw as { pendingSync?: boolean }).pendingSync === true;
    applyFields(raw);
    if (!wasAlreadyPending) {
      this.disk.addOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.MEDICAL_INQUIRY, 1);
    }
    // Rebuild the model so all derived fields (statusCodeFormatted, contactId, contactFormatted,
    // caseTypeFormatted, reportTypeFormatted, appointmentId) reflect the mutated rawData.
    // upsertMedicalInquiry preserves products via its carry-forward guard.
    const refreshed = new MedicalInquiry(raw);
    this.upsertMedicalInquiry(refreshed);
    return true;
  }

  /**
   * Update a medical inquiry offline. Queues the update for push on reconnect.
   * For offline-created MIs (have offlineCreatedAt), keeps pendingOperation as 'create' and only
   * updates pendingFormPayload so the create request uses the latest form data (e.g. after adding products).
   */
  updateMedicalInquiryOffline(id: string, formPayload: Record<string, unknown>): void {
    this._applyOfflinePendingChange(id, raw => {
      Object.assign(raw, formPayload);
      raw.indskr_medicalinquiryid = id;
      raw.pendingSync = true;
      const isOfflineCreated = raw.offlineCreatedAt != null;
      raw.pendingOperation = isOfflineCreated ? 'create' : 'update';
      raw.pendingFormPayload = formPayload;
    });
  }

  /**
   * Scrap (deactivate) a medical inquiry offline.
   * If the MI was created offline: on reconnect it is discarded locally only (no request to Dynamics).
   * If the MI already exists in Dynamics: queues the scrap for push on reconnect (deactivate in Dynamics).
   */
  async scrapMedicalInquiryOffline(id: string): Promise<void> {
    if (!id) return;
    const updated = this._applyOfflinePendingChange(id, raw => {
      raw.statecode = 1;
      raw.statuscode = MedicalInquiryStatus.INACTIVE;
      // Invalidate the stale OData formatted values so the reconstructed model's
      // statusCodeFormatted and any display surfaces refresh to "Inactive".
      delete raw['statuscode@OData.Community.Display.V1.FormattedValue'];
      delete raw['statuscode_Formatted'];
      delete raw['statecode@OData.Community.Display.V1.FormattedValue'];
      delete raw['statecode_Formatted'];
      raw.pendingSync = true;
      raw.pendingOperation = 'scrap';
    });
    if (!updated) return;
    // _applyOfflinePendingChange already reconstructed the model, persisted via upsertMedicalInquiry
    // (which filters inactive MIs, re-sorts, and emits medicalInquiries$). No additional patching needed.
  }

  /**
   * Clear the meeting/appointment link on a medical inquiry in local DB (and in-memory if still present).
   * Safe to call after scrap: works directly on the DB doc so it does not depend on the MI
   * still being in the in-memory list.
   */
  async clearMeetingLinkOffline(mi: MedicalInquiry): Promise<void> {
    if (!mi?.id) return;
    const dbKey = `${DB_KEY_PREFIXES.MEDICAL_INQUIRY}${mi.id}`;
    // Patch the DB document directly so the link is cleared on next load, regardless of memory state.
    await this.disk.updateOrInsert(dbKey, (existing: { raw?: Record<string, unknown> }) => {
      const raw: Record<string, unknown> = existing?.raw ? { ...existing.raw } : {};
      delete raw['indskr_appointment'];
      delete raw['_indskr_appointment_value'];
      delete raw['_indskr_appointment_value@OData.Community.Display.V1.FormattedValue'];
      return { raw };
    });
    // Also patch in-memory if still present
    const inMemory = this.medicalInquiries.find(m => m.id === mi.id);
    if (inMemory) {
      inMemory.appointmentId = '';
      if (inMemory.rawData && typeof inMemory.rawData === 'object') {
        const raw = inMemory.rawData as Record<string, unknown>;
        delete raw['indskr_appointment'];
        delete raw['_indskr_appointment_value'];
        delete raw['_indskr_appointment_value@OData.Community.Display.V1.FormattedValue'];
      }
    }
    // Also patch the passed MI object directly (may be the same ref or a stale ref)
    mi.appointmentId = '';
  }

  /**
   * Submit a medical inquiry offline (save signatures locally). Queues the submit for push on reconnect.
   */
  submitMedicalInquiryOffline(
    id: string,
    hcpSignature: string,
    repSignature: string,
    hcpSignatureDate?: string,
    repSignatureDate?: string,
    hcpSignatureTerms?: string
  ): void {
    this._applyOfflinePendingChange(id, raw => {
      raw.statuscode = MedicalInquiryStatus.SUBMITTED;
      // Invalidate the stale OData formatted value so the picklist label and the
      // reconstructed model's statusCodeFormatted are recomputed for SUBMITTED
      // (sub-observation 3b: online-created MI submitted offline kept showing "Draft").
      delete raw['statuscode@OData.Community.Display.V1.FormattedValue'];
      delete raw['statuscode_Formatted'];
      raw.indskr_hcpsignature = hcpSignature;
      raw.indskr_repsignature = repSignature;
      if (hcpSignatureDate) raw.indskr_hcpsignaturedate = hcpSignatureDate;
      if (repSignatureDate) raw.indskr_usersignaturedate = repSignatureDate;
      if (hcpSignatureTerms) raw.indskr_hcpsignatureterms = hcpSignatureTerms;
      raw.pendingSync = true;
      raw.pendingOperation = 'submit';
      raw.pendingSignatureData = { hcp: hcpSignature, rep: repSignature, hcpSignatureDate, repSignatureDate, hcpSignatureTerms };
    });
  }

  /**
   * Build a create-form payload from a master MI: copy fields/lookups, clear meeting link and signatures,
   * reset to draft timing, strip ids and audit fields. Prefers `pendingFormPayload` when present (offline shape).
   */
  buildMedicalInquiryCloneFormPayload(master: MedicalInquiry): Record<string, unknown> {
    const nowIso = new Date().toISOString();
    const raw = master?.rawData as Record<string, unknown> | undefined;
    if (!raw || typeof raw !== 'object') {
      return { indskr_reporteddate: nowIso };
    }

    const exclude = new Set([
      'indskr_medicalinquiryid',
      'id',
      'activityid',
      'statecode',
      'statuscode',
      'indskr_hcpsignature',
      'indskr_repsignature',
      'indskr_title',
      'indskr_appointment',
      '_indskr_appointment_value',
      '_indskr_appointment_value@OData.Community.Display.V1.FormattedValue',
      'createdon',
      'modifiedon',
      'createdby',
      'modifiedby',
      'modifiedonbehalfby',
      'createdonbehalfby',
      'versionnumber',
      'overriddencreatedon',
      'timezoneruleversionnumber',
      'utcconversiontimezonecode',
      'importsequencenumber',
      'ownerid',
      '_ownerid_value',
      '_ownerid_value@OData.Community.Display.V1.FormattedValue',
      'owningbusinessunit',
      'owninguser',
      'owningteam',
      'pendingSync',
      'pendingOperation',
      'pendingFormPayload',
      'pendingSignatureData',
      'offlineCreatedAt',
      'globalSearchedDataLinkEntities',
    ]);

    let base: Record<string, unknown>;
    const pending = raw.pendingFormPayload;
    if (pending && typeof pending === 'object' && !Array.isArray(pending)) {
      base = { ...(pending as Record<string, unknown>) };
    } else {
      base = {};
      const cloneScalarKeys = [
        'indskr_reporttype',
        'indskr_availableforreturn',
        'indskr_consumerrelated',
        'indskr_description',
        'indskr_ndc',
        'indskr_lotnumber',
        'indskr_packagesize',
        'indskr_expirydate',
        'indskr_dosageform',
        'indskr_strength',
        'indskr_primaryreporter',
        'indskr_autogeneratetitle',
        'indskr_usersignaturedate',
        'indskr_hcpsignatureterms',
        'indskr_hcpsignaturedate',
      ];
      for (const key of cloneScalarKeys) {
        if (raw[key] !== undefined) {
          base[key] = raw[key];
        }
      }
    }

    for (const k of exclude) {
      delete base[k];
    }
    delete base['indskr_Appointment@odata.bind'];
    delete base['indskr_appointment@odata.bind'];

    for (const key of [...Object.keys(base)]) {
      if (key === 'lookupfields') continue;
      if (key.startsWith('_') || key.includes('@')) {
        delete base[key];
      }
    }

    const lf = base.lookupfields;
    if (Array.isArray(lf)) {
      base.lookupfields = lf.filter((x: { name?: string }) => {
        const n = x?.name ? String(x.name).toLowerCase() : '';
        return n !== 'indskr_appointment' && n !== 'appointment';
      });
    } else {
      const built: Array<{ name: string; entity: string; id: string }> = [];
      const contactId = raw['_indskr_contact_value'] ?? base['_indskr_contact_value'];
      if (contactId != null && String(contactId).trim() !== '') {
        built.push({ name: 'indskr_contact', entity: 'contacts', id: String(contactId) });
      }
      const addressId = raw['_indskr_address_value'] ?? base['_indskr_address_value'];
      if (addressId != null && String(addressId).trim() !== '') {
        built.push({
          name: 'indskr_address',
          entity: 'indskr_addresses',
          id: String(addressId),
        });
      }
      const caseTypeId = raw['_indskr_casetype_value'] ?? base['_indskr_casetype_value'];
      if (caseTypeId != null && String(caseTypeId).trim() !== '') {
        built.push({
          name: 'indskr_casetype',
          entity: 'indskr_expertcategorieses',
          id: String(caseTypeId),
        });
      }
      if (built.length > 0) {
        base.lookupfields = built;
      }
    }

    const lfsNorm = base.lookupfields;
    if (Array.isArray(lfsNorm)) {
      const names = lfsNorm.map((x: { name?: string }) => String(x?.name || '').toLowerCase());
      const hasCase = names.some(n => n.includes('casetype'));
      const ct = raw['_indskr_casetype_value'];
      if (!hasCase && ct != null && String(ct).trim() !== '') {
        lfsNorm.push({
          name: 'indskr_casetype',
          entity: 'indskr_expertcategorieses',
          id: String(ct),
        });
      }
    }

    const lookupMirrorKeysToStrip = [
      '_indskr_contact_value',
      '_indskr_contact_value@OData.Community.Display.V1.FormattedValue',
      '_indskr_address_value',
      '_indskr_address_value@OData.Community.Display.V1.FormattedValue',
      '_indskr_casetype_value',
      '_indskr_casetype_value@OData.Community.Display.V1.FormattedValue',
    ];
    for (const k of lookupMirrorKeysToStrip) {
      delete base[k];
    }

    base.indskr_reporteddate = nowIso;
    return base;
  }

  /**
   * Clone a medical inquiry: new draft, no meeting link, no signatures, reported date now, active products copied.
   * Caller should add the returned MI to the agenda (`activityService.addActivity`) and handle navigation/refresh.
   */
  async cloneMedicalInquiryFromMaster(master: MedicalInquiry): Promise<MedicalInquiry> {
    const formPayload = this.buildMedicalInquiryCloneFormPayload(master);
    const productRows = await this.syncMedicalInquiryProductsForDisplay(master.id);
    const activeProducts = productRows.filter((p: Record<string, unknown>) => {
      const sc = p['indskr_medicalinquiryproduct.statecode'];
      return sc === undefined || sc === 0;
    });

    const cloneProductsForNewMi = async (newMi: MedicalInquiry): Promise<void> => {
      const onlineAdds: MedicalInquiryProductChange[] = [];
      for (const row of activeProducts) {
        const rec = row as Record<string, unknown>;
        const productId =
          rec['indskr_medicalinquiryproduct.indskr_product'] ??
          rec['indskr_medicalinquiryproduct.indskr_productid'] ??
          rec['_indskr_product_value'];
        if (productId == null || String(productId).trim() === '') continue;

        const values: Record<string, unknown> = {
          lookupfields: [
            { name: 'indskr_medicalinquiry', entity: 'indskr_medicalinquiries', id: newMi.id },
            { name: 'indskr_product', entity: 'products', id: String(productId) },
          ],
        };
        const formatted =
          rec['indskr_medicalinquiryproduct.indskr_product@OData.Community.Display.V1.FormattedValue'];
        const formStringValue =
          formatted != null && String(formatted).trim() !== ''
            ? { indskr_product: String(formatted) }
            : undefined;

        if (!this.deviceService.isOffline) {
          onlineAdds.push({ values, entity: MEDICAL_INQUIRY_PRODUCT_ENTITY, formStringValue });
          continue;
        }
        try {
          await this.addProductOffline(newMi.id, values, formStringValue);
        } catch (err: unknown) {
          this.logService.logError('MedicalInquiryDataService: clone product row failed', err);
        }
      }
      if (!onlineAdds.length) return;
      try {
        await this.saveMedicalInquiryWithProducts(newMi.id, onlineAdds);
      } catch (err: unknown) {
        this.logService.logError('MedicalInquiryDataService: clone products batch failed', err);
      }
    };

    if (this.deviceService.isOffline) {
      const newMi = this.createMedicalInquiryOffline(formPayload);
      await cloneProductsForNewMi(newMi);
      return newMi;
    }

    const res = (await this.createMedicalInquiry(formPayload)) as Record<string, unknown>;
    const idRaw = res?.indskr_medicalinquiryid ?? res?.guid ?? res?.id;
    const id = typeof idRaw === 'string' ? idRaw : idRaw != null ? String(idRaw) : '';
    if (!id) {
      throw new Error('cloneMedicalInquiryFromMaster: create returned no id');
    }
    let newMi = MedicalInquiry.fromApiResponse(res);
    this.upsertMedicalInquiry(newMi);
    await cloneProductsForNewMi(newMi);

    // When products were cloned, the server auto-generate plugin recalculates the title
    // to include product names. Re-fetch so the in-memory model has the updated title
    // for agenda, detail header, and edit form display.
    if (activeProducts.length > 0) {
      const refreshed = await this.getMedicalInquiryById(id, true);
      if (refreshed) {
        newMi = refreshed;
      }
    }

    return newMi;
  }

  /**
   * Merge all in-memory medical inquiries into the main ActivityService.activities list
   * so they appear in the agenda. Mirrors how SetBookingDataService feeds kit bookings
   * into the activity list during sync.
   */
  private mergeIntoActivityService(): void {
    for (const mi of this.medicalInquiries) {
      this.activityService.addActivity(mi, true, false, null, false);
    }
  }

  /**
   * Put a freshly fetched medical inquiry into the agenda store and repaint.
   *
   * `upsertMedicalInquiry` only reaches this service's own list and Pouch, while the agenda
   * list and the calendar chips both read `ActivityService.activities`. Without this, a
   * status changed in Dynamics shows in the detail pane — which renders the fetched record
   * directly — but nowhere else until the next sync runs mergeIntoActivityService().
   */
  private _syncRefreshedMiToAgenda(mi: MedicalInquiry): void {
    this.activityService.addActivity(mi, true, false, null, false);
    this.activityService.finalizeAgendaRefresh();
  }

  /**
   * Bulk-fetch all product records for the current user's active MIs and persist to Pouch.
   * Uses executeFetchQuery which auto-pages via fetchAll internally.
   *
   * Without `serverMiIds`: blind Pouch replace. Only safe when called for a true first-ever sync
   * (no local/offline history can exist yet).
   *
   * With `serverMiIds` (the set of MI ids the server told us about this cycle): merge-aware —
   * only product rows belonging to those ids are replaced (correctly clearing them if the server
   * now returns none), while rows for any other MI id are preserved untouched. Required whenever
   * this runs on a device that may already have offline history (e.g. a forced full refetch after
   * the org's MI form config changed) — a blind replace there could wipe product rows for an MI
   * whose create is still pending/deferred this cycle (see uploadPendingMedicalInquiries).
   */
  private async bulkSyncMyMiProducts(serverMiIds?: Set<string>): Promise<void> {
    try {
      const fetchXml = fetchQueries.medicalInquiry?.fetchMedicalInquiryProductsForOwner;
      if (!fetchXml) return;
      const result = await this.dynamics.executeFetchQuery(MEDICAL_INQUIRY_PRODUCT_ENTITY, fetchXml);
      const rows = Array.isArray(result) ? result : [];
      const normalized = rows.map((r: any) => this._normalizeProductRecordForDisplay(r));
      const dbKey = `${DB_KEY_PREFIXES.MEDICAL_INQUIRY_LINKED_ENTITY}indskr_medicalinquiryproduct`;

      if (serverMiIds) {
        const existing = await this.disk.retrieve(dbKey);
        const localRaw = Array.isArray(existing?.raw) ? existing.raw : [];
        const preserved = localRaw.filter((r: any) => !serverMiIds.has(r?.indskr_medicalinquiryid));
        const merged = [...preserved, ...normalized];
        await this.disk.updateOrInsert(dbKey, () => ({ raw: merged }));
        this.populateMiProductsFromRows(merged);
      } else {
        await this.disk.updateOrInsert(dbKey, () => ({ raw: normalized }));
        this.populateMiProductsFromRows(normalized);
      }
    } catch (err: unknown) {
      this.logService.logError('MedicalInquiryDataService: bulkSyncMyMiProducts failed', err);
    }
  }

  /**
   * Read product rows from Pouch linked-entity doc and populate mi.products on all in-memory My MIs.
   * Called on offline / DB-only load path after medicalInquiries is populated.
   */
  private async populateMiProductsFromDb(): Promise<void> {
    try {
      const dbKey = `${DB_KEY_PREFIXES.MEDICAL_INQUIRY_LINKED_ENTITY}indskr_medicalinquiryproduct`;
      const doc = await this.disk.retrieve(dbKey);
      const rows = Array.isArray(doc?.raw) ? doc.raw : [];
      this.populateMiProductsFromRows(rows);
    } catch (err: unknown) {
      this.logService.logError('MedicalInquiryDataService: populateMiProductsFromDb failed', err);
    }
  }

  /**
   * Group active product rows by MI ID and set mi.products on each in-memory My MI.
   * Shared by bulk sync (online) and DB load (offline) paths.
   */
  private populateMiProductsFromRows(rows: Record<string, any>[]): void {
    const byMiId = new Map<string, Array<{ id: string; name: string }>>();
    for (const row of rows) {
      const miId: string = row['indskr_medicalinquiryid'];
      const productId: string = row['indskr_medicalinquiryproduct.indskr_product']
        ?? row['indskr_medicalinquiryproduct.indskr_productid'];
      const name: string =
        row['indskr_medicalinquiryproduct.indskr_product@OData.Community.Display.V1.FormattedValue'] ?? '';
      const sc = row['indskr_medicalinquiryproduct.statecode'];
      if (!miId || !productId || (sc !== undefined && sc !== 0)) continue;
      if (!byMiId.has(miId)) byMiId.set(miId, []);
      byMiId.get(miId).push({ id: productId, name });
    }
    for (const mi of this.medicalInquiries) {
      mi.products = byMiId.get(mi.id) ?? [];
    }
  }

  /**
   * Bulk-fetch products for all team MIs in a single Dynamics call, then distribute onto each MI object.
   * No Pouch write — results are kept in-memory on each team MI object only.
   * Called after fetchTeamMedicalInquiries on team tab click.
   */
  async fetchTeamMiProducts(teamMis: MedicalInquiry[], childUserIds: string[]): Promise<void> {
    if (!teamMis.length || !childUserIds.length) return;
    try {
      const template = fetchQueries.medicalInquiry?.fetchMedicalInquiryProductsForTeam;
      if (!template) return;
      const childUserValues = childUserIds.map(id => `<value>${id}</value>`).join('');
      const fetchXml = template.replace('{childUserValues}', childUserValues);
      const result = await this.dynamics.executeFetchQuery(MEDICAL_INQUIRY_PRODUCT_ENTITY, fetchXml);
      const rows = Array.isArray(result) ? result : [];
      const normalized = rows.map((r: any) => this._normalizeProductRecordForDisplay(r));

      const byMiId = new Map<string, Array<{ id: string; name: string }>>();
      for (const row of normalized) {
        const miId: string = row['indskr_medicalinquiryid'];
        const productId: string = row['indskr_medicalinquiryproduct.indskr_product']
          ?? row['indskr_medicalinquiryproduct.indskr_productid'];
        const name: string =
          row['indskr_medicalinquiryproduct.indskr_product@OData.Community.Display.V1.FormattedValue'] ?? '';
        const sc = row['indskr_medicalinquiryproduct.statecode'];
        if (!miId || !productId || (sc !== undefined && sc !== 0)) continue;
        if (!byMiId.has(miId)) byMiId.set(miId, []);
        byMiId.get(miId).push({ id: productId, name });
      }
      for (const mi of teamMis) {
        mi.products = byMiId.get(mi.id) ?? [];
      }
    } catch (err: unknown) {
      this.logService.logError('MedicalInquiryDataService: fetchTeamMiProducts failed', err);
    }
  }

  /**
   * Add or update a medical inquiry in the in-memory list and DB.
   * Call after create/update from ind-dynamic-form or API.
   */
  upsertMedicalInquiry(mi: MedicalInquiry): void {
    const idx = this.medicalInquiries.findIndex(m => m.id === mi.id);
    if (idx >= 0) {
      // Carry forward products when the replacement is a freshly constructed MI with empty products.
      // This prevents API update/submit/upload results from wiping the filter data.
      if (!mi.products?.length && this.medicalInquiries[idx].products?.length) {
        mi.products = this.medicalInquiries[idx].products;
      }
      this.medicalInquiries[idx] = mi;
    } else {
      this.medicalInquiries.unshift(mi);
    }
    this.medicalInquiries = this.medicalInquiries.filter(m => m.isActive || m.isCompleted || m.isSubmitted);
    this.medicalInquiries.sort((a, b) => {
      const dateA = a.modifiedOn ? a.modifiedOn.getTime() : 0;
      const dateB = b.modifiedOn ? b.modifiedOn.getTime() : 0;
      return dateB - dateA;
    });
    this.medicalInquiries$.next(this.medicalInquiries);
    if (mi?.id) {
      this.disk.updateOrInsert(`${DB_KEY_PREFIXES.MEDICAL_INQUIRY}${mi.id}`, () => ({ raw: mi.rawData || { ...mi } }));
    }
  }
}

